import { z } from 'zod';
import { baseListQuery, uuidSchema } from './common';
import { ROLES, roleLabels, roleSchema, type Role } from './enums';
import { grantScopeRule, roleScopeAxis, roleScopeAxisLabels } from './grant-scope';
import { PERMISSION_CATALOG, permissionModuleLabels } from './permission-catalog';
import { PERMISSIONS, type Permission, type PermissionDelta } from './permissions';
import type { AddonScopeModule, RoleAddon } from './role-addons';

/**
 * Назначаемые полномочия (ADR 0106) — именованный набор прав, который выдаётся учётке поверх её
 * роли и приходит на смену надстройкам (ADR 0086).
 *
 * Файл заводит **данные и решения, но не поведение доступа**: коды двух системных наборов, границы
 * свободной сборки, таблицы, по которым сервер, миграция и будущий конструктор принимают решения, и
 * единую проверку выдачи, которая собирает все барьеры в одном месте (`validateGrantAssignment`,
 * этап 3, первая волна). Матрица `ROLE_PERMISSIONS`, список `ROLE_ADDONS` и поведение `can` здесь не
 * меняются ни на одно право — эффективный доступ живых учёток прежний, а проверку выдачи пока никто
 * не спрашивает: маршруты и экран приходят следующими волнами.
 *
 * Отдельным файлом от матрицы по той же причине, по которой отделён каталог: `permissions.ts`
 * отвечает на вопрос «кому положено право», а здесь — «что нельзя выдать», «что без чего не имеет
 * смысла» и «что нельзя свести в одном человеке». Зависимость односторонняя: полномочия знают про
 * словарь прав, словарь про полномочия — нет.
 */

/**
 * Коды двух системных полномочий, переносимых из надстроек (ADR 0106, решение 9, шаг 1a).
 *
 * Код — идентификатор набора, а не его название, и стабилен он не из аккуратности: по коду
 * миграция шага 1a создаёт набор именно под этим кодом; по коду двойная запись
 * отражает выдачу надстройки в `user_grants`; по коду типизированная таблица расширения области
 * (`GRANT_MODULE_WIDE_SCOPE`, ADR 0106, решение 2) вешает на «Согласование ИТ» сквозную область
 * двух модулей оргтехники (ADR 0096). Название, описание и цвет — витрина, их правит выкат;
 * связывает строку в базе с кодом только код, и второго такого ключа нет.
 *
 * Отсюда запрет переименовывать системный набор в базе. `UPDATE grants SET code = ...` не
 * «переименовывает набор», а отвязывает его от кода: таблица области перестаёт его находить, и
 * виза ИТ теряет сквозную область, оставаясь на вид выданной. Отказ при этом молчаливый — прав у
 * держателя ровно столько же, а видит он снова только свой отдел, и объяснить это по журналу
 * выдач невозможно, потому что выдачу никто не трогал. По той же причине системный набор не
 * редактируется и не удаляется из интерфейса вовсе: его состав и код живут в коде, а строка в
 * базе — их отражение, а не источник.
 *
 * `satisfies readonly RoleAddon[]` — мост перехода, а не украшение. Пока идут шаги 1a–1d, код
 * набора обязан совпадать с кодом надстройки, из которой он перенесён: иначе двойной записи некуда
 * вставлять назначение, а сверка шага 1b покажет diff на ровном месте. Проверка уходит вместе с
 * `role-addons.ts` на шаге 1e.
 */
export const SYSTEM_GRANT_CODES = [
  'office_equipment_operator',
  'office_equipment_it_approver',
] as const satisfies readonly RoleAddon[];

export type SystemGrantCode = (typeof SYSTEM_GRANT_CODES)[number];

/**
 * Модуль, в котором набор может снимать сужение области.
 *
 * Псевдоним `AddonScopeModule`, а не второй список из тех же двух имён. Пока идут шаги 1a–1d,
 * таблица области существует в двух видах — по надстройкам и по кодам наборов, — и разъехавшийся
 * перечень модулей означал бы, что «сквозная область» до переключения читателей и после неё
 * означает разное. Собственный список появится здесь на шаге 1e, когда `role-addons.ts` уйдёт
 * целиком и переносить будет уже нечего.
 */
export type ScopeModule = AddonScopeModule;

/**
 * Модули, в которых набор снимает сужение области (ADR 0106, решение 2), — та же таблица, что
 * `ADDON_MODULE_WIDE_SCOPE`, но по **кодам наборов**, а не по значениям enum'а надстроек.
 *
 * Ключ — `SystemGrantCode`, и это не украшение типа, а вся суть решения. Расширение области —
 * кодовое свойство конкретного набора, а не то, что администратор отмечает в конструкторе:
 * пользовательский набор scope-neutral без исключений, и собрать «видеть чужой объект» в
 * интерфейсе невозможно. Держится это тем, что чужой ключ здесь не примет компилятор, — а не
 * проверкой `is_system` при чтении, которую однажды забудут поставить.
 *
 * Пустой список у оператора оргтехники — не забытая строка, а решение 2 ADR 0086 в неизменном
 * виде: он ведёт свою площадку и свой отдел.
 */
export const GRANT_MODULE_WIDE_SCOPE: Record<SystemGrantCode, readonly ScopeModule[]> = {
  office_equipment_operator: [],
  office_equipment_it_approver: ['serviceRequests', 'officeEquipment'],
};

/**
 * Тот же перечень, разложенный по ключу-строке. `Map`, а не индексация `Record` с приведением
 * `code as SystemGrantCode`: приведение пообещало бы, что в наборах учётки бывают только системные
 * коды, а приходят они из базы, где рядом лежат собранные администратором.
 */
const MODULE_WIDE_SCOPE_BY_CODE: ReadonlyMap<string, readonly ScopeModule[]> = new Map(
  Object.entries(GRANT_MODULE_WIDE_SCOPE),
);

/**
 * Видит ли держатель наборов модуль целиком, минуя область своей роли.
 *
 * На вход идут коды **из базы** — обычные строки, а не `SystemGrantCode`. Пользовательский набор,
 * набор с кодом из будущего выката, набор, переименованный в базе руками, — всё это даёт
 * `undefined` в таблице и никакого расширения. Сузить параметр до системных кодов значило бы
 * требовать от читателя разбирать коды до вызова, то есть завести вторую границу там, где хватает
 * промаха по ключу.
 *
 * **Гейта по роли здесь нет, и это осознанно.** Сегодняшний `hasModuleWideScope` роль тоже не
 * спрашивает: совместимость набора с ролью — вопрос выдачи, и стоит он там, где выдают. Добавить
 * проверку заодно с переносом значило бы изменить поведение под видом переезда, а шаг перехода
 * обязан менять способ хранения и ничего больше. Совместимость с ролью на чтении при этом никуда
 * не делась — её держит выражение прав (`grantPermissionsExpr`, соединение с `grant_roles`); здесь
 * же речь про область, и заводить второй гейт с другим ответом нельзя.
 */
export function hasModuleWideScope(
  codes: readonly string[] | null | undefined,
  module: ScopeModule,
): boolean {
  return (codes ?? []).some((code) => MODULE_WIDE_SCOPE_BY_CODE.get(code)?.includes(module));
}

// ── Административная часть стартового каталога (план §10 и §15, этап 4) ──

/**
 * Объявленный состав системного набора — то, что миграция каталога переписывает в базу строками
 * `INSERT`, а страж `grants-catalog.db.test.ts` сверяет обратно.
 *
 * Описания здесь нет намеренно, и это не экономия. Описание — абзац для администратора, который
 * читается на экране каталога рядом с набором и объясняет **работу**, а не доступ; его место — в
 * миграции, где оно стоит вместе с обоснованием строки. Название, состав и роли — другое дело: на
 * них ссылается проверка выдачи (`grantLabel` в тексте отказа), их сверяет страж, и разъехавшись,
 * они меняют доступ. Копия, которую никто не сверяет, хуже отсутствия копии.
 */
export interface CatalogGrant {
  readonly name: string;
  readonly permissions: readonly Permission[];
  readonly roles: readonly Role[];
}

/**
 * Коды четырёх административных наборов (план §10, «разгружающие администратора»).
 *
 * Отдельным списком от `SYSTEM_GRANT_CODES`, а не строками в нём, — по той же причине, по которой
 * тот объявлен `satisfies readonly RoleAddon[]`. Те два кода существуют ради **перехода**: по ним
 * двойная запись отражает выдачу надстройки, по ним же типизирована таблица сквозной области
 * (`GRANT_MODULE_WIDE_SCOPE`), и на шаге 1e они уйдут вместе с `role-addons.ts`. Административные
 * наборы ни к какой надстройке не относятся, сквозной области не несут и переживут переход;
 * дописать их туда значило бы соврать компилятору дважды — про надстройку, которой нет, и про
 * область, которой набор не даёт.
 *
 * Коды придуманы латиницей и стабильны навсегда: по коду набор находят миграции каталога, журнал
 * действий и реестр выдач (см. `grants.code`). Название меняется выкатом, код — нет.
 */
export const ADMIN_GRANT_CODES = [
  'directory_exchange',
  'archivist',
  'auditor',
  'strict_report_blanks',
] as const;

export type AdminGrantCode = (typeof ADMIN_GRANT_CODES)[number];

/**
 * Четыре набора, разгружающие администратора, — состав ровно по плану §10.
 *
 * **Ни один из них никому не выдаётся.** Этап 4 заводит наборы и на этом заканчивается: колонка
 * «расширяет доступ» у него — «нет», и живые права проверяются тем, что миграция не создаёт ни
 * одной строки `user_grants` (страж в `grants-catalog.db.test.ts`). Список ролей отвечает на
 * «кому набор можно назначить», а не «кому он уже назначен».
 *
 * **Роли — `dispatcher` и `manager`, хотя план §10 называет одного диспетчера.** Сегодня это две
 * живые роли, совпадающие по правам во всём, кроме четырёх диспетчерских (§9), и ни одно из
 * административных прав в эту четвёрку не входит: администратора разгружает диспетчерская целиком,
 * а не половина её людей. Таблица плана написана по целевой модели, где менеджера уже нет
 * (решение №2, §16), — а до слияния «только диспетчер» означало бы, что набор нельзя выдать тому,
 * ради кого он заведён. Слияние ролей эти строки не сломает: `manager` станет мёртвым значением
 * enum, а строка `grant_roles` — мёртвой вместе с ним, и убирается она тем же этапом 7.
 */
export const ADMIN_GRANTS: Record<AdminGrantCode, CatalogGrant> = {
  /**
   * Выгрузка и загрузка справочника файлом (`routes/directory-transfer.ts`). Пара, а не два
   * набора: обмен — одна работа, и загрузка без выгрузки означала бы правку справочника вслепую,
   * без возможности сперва посмотреть, что в нём лежит.
   */
  directory_exchange: {
    name: 'Обмен справочниками',
    permissions: ['directories.export', 'directories.import'],
    roles: ['dispatcher', 'manager'],
  },
  /**
   * Удалённые записи и возврат их из архива. Оба права вместе намеренно: `archive.restore` без
   * `archive.read` — возврат вслепую (удалённой записи держатель не видит вовсе, модуль отвечает
   * ей 404), а разведённые по двум наборам они дали бы администратору выбор, которого в работе
   * архивариуса не существует.
   *
   * Откат статуса заявки (`requests.rollbackStatus`) сюда **не** входит и приедет своим набором на
   * этапе 4б: выдать архивариуса ради отката значило бы открыть заодно удалённые записи (§10).
   */
  archivist: {
    name: 'Архивариус',
    permissions: ['archive.read', 'archive.restore'],
    roles: ['dispatcher', 'manager'],
  },
  /**
   * Журнал действий (`routes/audit.ts`). Одно право — не признак лишней сущности: чтение журнала
   * самостоятельная работа, и `PERMISSION_REQUIRES['audit.read']` объявляет его входным именно
   * затем, чтобы набор не требовал `users.manage` модуля витрины — права, которое не выдаётся
   * вовсе.
   */
  auditor: {
    name: 'Аудитор',
    permissions: ['audit.read'],
    roles: ['dispatcher', 'manager'],
  },
  /**
   * Пустой бланк — лист без заявок (ADR 0071). Право спрашивают обработчики выписки листа
   * (`routes/vehicle-routes.ts`), а сами ручки закрыты `waybills.read` и `vehicleRequests.status`:
   * набор поэтому осмыслен только у того, кто журнал уже ведёт, — и обе объявленные роли ведут.
   *
   * `waybills.correctBeyondLimit` рядом не стоит и стоять не будет: предел в тридцать дней — это и
   * есть граница, за которой решает администратор (§10, `NON_GRANTABLE_PERMISSIONS`).
   */
  strict_report_blanks: {
    name: 'Бланки строгой отчётности',
    permissions: ['waybills.issueBlank'],
    roles: ['dispatcher', 'manager'],
  },
};

// ── Ролевая часть стартового каталога (план §10 и §15, этап 4б; ADR 0112) ──

/**
 * Коды трёх наборов, замещающих упраздняемые роли площадки и отдела (план §10, «замещающие
 * упразднённые роли»).
 *
 * Третьим списком рядом с `SYSTEM_GRANT_CODES` и `ADMIN_GRANT_CODES`, а не строками в них, — по той
 * же причине, по которой разведены те два. Первый существует ради **перехода**: его коды обязаны
 * совпадать с кодами надстроек (`satisfies readonly RoleAddon[]`), и на шаге 1e он уйдёт вместе с
 * `role-addons.ts`. Второй заводит наборы, ни от чего не зависящие. Эти три отвечают за третье и
 * своё: по ним **этапы 8 и 9 переводят учётки** — миграция перевода ищет набор по коду и выдаёт его
 * тем, у кого была прежняя роль. Смешай их с административными — и перевод пришлось бы описывать
 * выборкой «те четыре не берём», то есть повторять границу, которую здесь достаточно объявить.
 *
 * Коды латиницей и стабильны навсегда: на них ссылаются миграция каталога, миграции перевода ролей,
 * журнал действий и реестр выдач. Название правится выкатом, код — нет.
 */
export const ROLE_GRANT_CODES = [
  'vehicle_ordering',
  'site_approval',
  'department_approval',
  /*
   * Четвёртый — «Заказ механизации» (план `docs/mechanization-approval-and-grants-plan.md`, Р7):
   * им модуль аренды уходит из состава роли `site` и начинает выдаваться поимённо.
   *
   * Список **ролевой**, а не модульный, и это решение, а не оформление. Модульные наборы делят
   * работу внутри модуля между теми, кто в нём и так работает; этот замещает часть упраздняемой
   * роли и участвует в тождествах перевода (`ROLE_MIGRATIONS`) — попади он в модульный список,
   * таблица перевода не смогла бы на него сослаться типом.
   */
  'mech_ordering',
] as const;

export type RoleGrantCode = (typeof ROLE_GRANT_CODES)[number];

/**
 * Три набора, замещающие упраздняемые роли, — состав ровно по плану §10.
 *
 * **Ни один из них никому не выдаётся, и роль `site` на момент их заведения не назначена ни одной
 * учётке.** Этап 4б заводит роль и каталог и на этом заканчивается: колонка «расширяет доступ» у
 * него — «нет». Доступ живых учёток меняется только этапами 8 и 9, где перевод ролей выдаёт эти
 * наборы явно и с записью в реестр.
 *
 * **Почему роль вводится здесь, а не вместе с переводом.** Колонка `grant_roles` — это внешний ключ
 * в значение enum'а `role`: строку с несуществующим значением база не примет. Отложи мы роль до
 * этапа 8 — каталог пришлось бы либо заводить без совместимых ролей (два набора, которые никому
 * нельзя выдать), либо создавать двумя частями, нарушив правило реформы «наборы заводятся ровно
 * один раз» (§16.1). Оба варианта отвергнуты ADR 0112.
 *
 * **Разделение состава на «ведение» и «визу» — решение, а не оформление** (§10). Виза двигает
 * сроки заказов и применяет недельный пакет необратимо, той же транзакцией; набор, где она лежит
 * рядом с ведением, означал бы, что собравший неделю сам её и подписал.
 */
export const ROLE_GRANTS: Record<RoleGrantCode, CatalogGrant> = {
  /**
   * Ведение заказов техники и недельного пакета — то, чем `shtab` отличается от `commandant`.
   * Модуля два, а набор один: недельная заявка — документ-основание над заказами ТС, и площадка,
   * которая заказывает технику, тем же решением каждую неделю говорит, что из стоящей на ней
   * остаётся. Развести их по двум наборам значило бы предложить администратору выбор, которого в
   * работе площадки не существует.
   *
   * Удаления недели в составе нет, и это не пропуск: у документа его нет вовсе — до применения
   * заявка снимается отменой с причиной, после она история (`WEEKLY_REQUEST_PERMISSIONS`).
   */
  vehicle_ordering: {
    name: 'Заказ техники',
    permissions: [
      'vehicleRequests.read',
      'vehicleRequests.create',
      'vehicleRequests.update',
      'vehicleRequests.delete',
      'weeklyRequests.read',
      'weeklyRequests.create',
      'weeklyRequests.update',
    ],
    roles: ['site'],
  },
  /**
   * Подпись площадки — то, чем `rukstroy` отличается от `shtab`, и с планом визы механизации
   * (`docs/mechanization-approval-and-grants-plan.md`, Р2) прав в нём три: заказ, неделя и аренда.
   * Все подписи вместе намеренно: у объекта это одна должность и одна ответственность —
   * руководитель строительства подписывает и отдельный заказ, и недельный пакет, который заказы
   * порождает, и аренду виброплиты, за которую площадка платит посуточно. Второго набора «Виза
   * механизации» рядом нет по той же причине: администратору предлагали бы выбор, которого в
   * работе площадки не существует.
   *
   * Набор осмыслен только поверх заказных наборов, и держит это не порядок в форме, а барьер
   * требований: все три визы объявлены в `PERMISSION_REQUIRES` зависящими от чтения своего модуля.
   * Выдача «Визы объекта» роли, у которой заказов не видно, отвечает `requirement_missing` и
   * называет недостающее право. С Э2 плана визы таких наборов становится два — «Заказ техники» и
   * «Заказ механизации», — потому что механизация уходит из состава роли `site`.
   */
  site_approval: {
    name: 'Виза объекта',
    permissions: ['vehicleRequests.approve', 'weeklyRequests.approve', 'mechRequests.approve'],
    roles: ['site'],
  },
  /**
   * Подпись отдела — то, чем `department_head` отличается от `department`, и с планом визы
   * механизации прав в нём два: заказ техники и аренда своего отдела (план визы, Р5).
   *
   * **Две визы, а не одна, и это решение §10.** Состав у них разный не по недосмотру: у объекта
   * подпись двигает сроки заказов и применяет недельный пакет, у отдела — только заказ. Неделя
   * описывает занятость площадки, решает по ней площадка, и отдел её лишь читает (ADR 0062).
   * Полномочие с составом, зависящим от роли держателя, было бы неявным доступом: администратор
   * читал бы в каталоге одно, а выдавал разное.
   *
   * Аренда механизации попадает сюда, а решение о её продлении — нет: продление идущей аренды
   * подписывает площадка, на которой техника стоит (план визы, Р10), и права на это отдельного
   * нет вовсе — область считает предикат, а не второе право.
   *
   * **Набор действует уже сегодня** (совместимая роль `department` живая, а не целевая), поэтому
   * его держатели получают визу механизации в момент выката — объявленное расширение доступа,
   * названное планом визы, Р2.
   */
  department_approval: {
    name: 'Виза отдела',
    permissions: ['vehicleRequests.approve', 'mechRequests.approve'],
    roles: ['department'],
  },
  /**
   * Аренда малой механизации, которую площадка просит, правит и снимает (план визы, Р7). Хода
   * аренды (`.status`) и продления (`.extend`) в наборе нет: их решает офис (ADR 0152, Р9), и
   * набор, раздавший бы их площадке, отвечал бы за заказчика на вопрос, которого он не задавал.
   * Визы (`mechRequests.approve`) нет тем более — подписывают не все, кто заказывает, и живёт она
   * в «Визе объекта» рядом с подписями заказа техники и недели.
   *
   * **Совместимая роль одна — `site`**, и это не осторожность, а действующее правило реформы:
   * ролевой набор не объявляется совместимым с упраздняемой ролью (`role-migration-contracts`),
   * иначе релиз, который никого не переводит, расширил бы множество того, что вообще можно выдать.
   * Отсюда прямое следствие, названное планом: до перевода учёток модуль остаётся по роли у
   * `shtab`, `rukstroy` и `commandant`, а набор приезжает им **взведённым** (`ROLE_MIGRATIONS`) и
   * начинает действовать вместе со сменой роли.
   *
   * Осмыслен он и сам по себе: чтение модуля приходит вместе с ведением, и барьер требований
   * (`PERMISSION_REQUIRES`) выдаче в одиночку не мешает — в отличие от «Визы объекта», которой с
   * этого этапа нужны ОБА заказных набора.
   */
  mech_ordering: {
    name: 'Заказ механизации',
    permissions: [
      'mechRequests.read',
      'mechRequests.create',
      'mechRequests.update',
      'mechRequests.delete',
    ],
    roles: ['site'],
  },
};

// ── Модульная часть стартового каталога (план переделки цикла заявок оргтехники, §7.2) ──

/**
 * Коды наборов, которыми делится работа внутри модуля, — четвёртым списком рядом с тремя
 * предыдущими.
 *
 * **В `SYSTEM_GRANT_CODES` такой код добавить нельзя**: тот объявлен `satisfies readonly RoleAddon[]`
 * и принимает только коды перенесённых надстроек. Надстройки «Оргтехника: номенклатура» не было и
 * не будет — набор заводится сразу набором, — и приписка его туда соврала бы компилятору про
 * надстройку, которой нет, а заодно потребовала бы ключа в `GRANT_MODULE_WIDE_SCOPE`, где ему
 * нечего сказать. К административным он не относится тем более: те разгружают администратора
 * портала, а этот делит работу внутри модуля между теми, кто в нём и так работает.
 *
 * Отдельным списком, а не строкой в `ROLE_GRANT_CODES`: по тем миграции этапов 8 и 9 **переводят
 * учётки** с упраздняемых ролей, и набор, попавший туда, перевод бы и получил — то есть достался бы
 * каждому, кого переводят, вместо одного человека, которому номенклатуру ведут поимённо.
 *
 * **Автозапчастей в этом списке нет, и это решение, а не очередь** (план автозапчастей, Р10;
 * план чеков, Р4). Право `autoParts.manage` выдано ролями `mechanic` и `chief_mechanic`
 * (`permissions.ts`), и разница с расходниками ровно в постановке: там номенклатуру ведёт один
 * человек, которого назначают поимённо, — поэтому набор и заведён; здесь заказчик назвал не
 * человека, а службу, а служба в портале уже описана двумя ролями. Набор ради аудитории,
 * совпадающей с ролями, добавил бы администратору шаг выдачи, ничего не разделив, и создал бы
 * второй ответ на вопрос «кто ведёт чеки» — набор и роль, расходящиеся при первой же правке.
 *
 * **Второй код — аудит распознавания талонов вывоза** (`docs/waste-ticket-audit-plan.md` §4.1), и
 * список остался четвёртым, а не стал пятым. Пятый пришлось бы завести, будь у набора своя
 * механика: у `SYSTEM_GRANT_CODES` это мост перехода и ключ таблицы сквозной области, у
 * `ROLE_GRANT_CODES` — миграции перевода учёток, которые ищут наборы по этому самому списку. У
 * аудита такой механики нет ни одной: его никто не ищет отдельно, и пятый перечень отличался бы от
 * четвёртого только заголовком, зато требовал бы строки в `ALL_SYSTEM_GRANT_CODES` — то есть завёл
 * бы ещё одно место, где страж базы замолкает, если про него забыли.
 *
 * По смыслу он ровно здесь: аудит — работа **внутри** модуля вывоза (насколько верно портал читает
 * талоны и что приходится править руками), и достаётся она одному человеку из тех, кто в модуле и
 * так работает. К административным он не относится по той же границе, что и номенклатура: те
 * разгружают администратора портала от чужой работы — журнала действий, архива, обмена
 * справочниками, — а этот делит работу внутри модуля.
 *
 * **Третий код — работа исполнителем в оргтехнике** (`office_equipment_executor`, план
 * `docs/office-equipment-access-profiles-plan.md`, Р4; план аудита исполнителей, Р1 и Э8), и он
 * здесь по той же границе, что первые два: делит работу ВНУТРИ модуля между теми, кто в нём уже
 * работает. К `SYSTEM_GRANT_CODES` его не приписать вдвойне — надстройки «исполнитель» не было и
 * не будет, а ключ в `GRANT_MODULE_WIDE_SCOPE` был бы прямым отрицанием того, ради чего набор
 * заведён: сквозной области у исполнителя нет и быть не может, заявку ему открывает поимённое
 * назначение. Компилятор это и стережёт — таблица области типизирована `SystemGrantCode`, и
 * модульный код в неё не пройдёт; комментария об этом решении мало, потому что комментарий не
 * падает.
 *
 * Он же — единственный модульный набор, которому миграция создаёт назначения: разделение
 * ИТ-набора обязано было сохранить работу действующим держателям (см. `MODULE_GRANTS` ниже).
 *
 * **Четвёртый код — заявитель оргтехники** (`office_equipment_requester`, план профилей, Р4 и Р6;
 * этап Э7), и он здесь по той же границе, что первые три: делит работу внутри модуля. Граница у
 * него, впрочем, своя — он единственный набор каталога, заведённый ради ролей, у которых модуля
 * НЕТ ВОВСЕ. Для штаба, площадки и отдела «Заявитель» — базовое состояние роли
 * (`SERVICE_REQUEST_CUSTOMER_PERMISSIONS` плюс `officeEquipment.read`), никакой выдачи не
 * требующее; набор нужен ровно тем, ради кого пункт разбора и написан: диспетчер видит справочник
 * оргтехники, но завести заявку не может, — а именно он сидит в офисе рядом с принтером.
 *
 * ОТВЕРГНУТО ТОГДА, И ПОЛОВИНУ ЭТОГО ОТМЕНИЛИ ПОТОМ: дописать `serviceRequests.*` в роли
 * `manager`/`dispatcher`. Довод был и остаётся верным — право, пришедшее ролью, снимается только
 * выкатом, тогда как набор снимается галочкой, с автором и временем в реестре выдач. Но 10.09.2026
 * заказчик решил иначе про менеджера, и решение это перевешивает довод по причине, которой здесь
 * не было: `manager` — временная роль-заплатка, и всё, что она раздаёт, обязано уйти вместе с её
 * строкой, а не пережить её мусором в `grant_roles` и `user_grants`
 * ([ADR 0181](../../../docs/adr/0181-manager-service-requester.md)). Про диспетчера отказ в силе:
 * его роль никто убирать не собирается, и круг ему по-прежнему приходит набором.
 *
 * К `SYSTEM_GRANT_CODES` он не относится по тем же двум причинам, что исполнитель: надстройки
 * «заявитель» не было и не будет, а ключ в `GRANT_MODULE_WIDE_SCOPE` означал бы сквозную область —
 * то, чего у заявителя нет ни в одном виде. Область у его держателя и без набора глобальна на
 * ЧТЕНИЕ (роль без оси), а изменяющие действия сужает страж авторства
 * (`actsAsRequestCustomer`, Р6), и живёт он в предикатах, а не в таблице области.
 *
 * Код латиницей и стабилен навсегда: по нему набор находят миграция каталога, журнал действий и
 * реестр выдач. Название правится выкатом, код — нет.
 */
export const MODULE_GRANT_CODES = [
  'office_equipment_consumables',
  'office_equipment_executor',
  'office_equipment_requester',
  'waste_ticket_audit',
  /*
   * Шестой код — разбор карантина файлов (план
   * `docs/office-equipment-on-site-and-invoice-estimate-plan.md`, Р6, п. 4). Модуль у него `files`,
   * а не оргтехника, хотя приехал он с волной освобождения от подписи: карантин ставят по ФАЙЛУ, и
   * файл приходит из любого модуля портала — положи набор в оргтехнику, и вывоз с путевыми листами
   * остались бы без разбора инцидента.
   *
   * Модульным, а не административным, — по той же границе, что у аудита талонов: административные
   * четыре снимают с администратора работу, которая администратора не требует, а эта требует его
   * ровно настолько, насколько требует названного человека. Набор здесь — единственный способ
   * назвать его поимённо.
   */
  'files_quarantine_audit',
  /*
   * Седьмой — просмотр и печать путевых листов площадкой и отделом (ADR 0192). Модульный, а не
   * ролевой, и выбор здесь не оформительский: ролевые наборы участвуют в тождествах перевода
   * (`ROLE_MIGRATIONS`) и потому не объявляются совместимыми с упраздняемой ролью — а этот обязан
   * действовать **до** перевода, на живых `shtab`, `rukstroy` и `commandant`. Заказчику бумага
   * нужна сейчас, а не релизом позже; попади код в `ROLE_GRANT_CODES`, страж ролевых наборов
   * потребовал бы ровно обратного.
   *
   * Работу внутри модуля он тоже делит — по той же границе, что аудит талонов: диспетчерская ведёт
   * журнал целиком, площадка смотрит и печатает своё. Это и есть признак модульного набора.
   */
  'waybills_view',
] as const;

export type ModuleGrantCode = (typeof MODULE_GRANT_CODES)[number];

/**
 * Наборы модульной части — по одному на свою постановку: номенклатура расходников по таблице §7.2
 * плана переделки цикла заявок оргтехники, аудит распознавания — по §4.1 плана аудита талонов,
 * разбор карантина файлов — по Р6, п. 4 плана освобождения от подписи.
 *
 * **Никому не выдаются — кроме одного случая, и он назван.** Миграция каталога заводит набор,
 * состав и строки `grant_roles`, а `user_grants` не создаёт: работу ведёт названный человек, и
 * называет его администратор портала строкой с автором и временем, а не миграция списком ролей.
 * Единственное исключение — `office_equipment_executor`: он не заводится с нуля, а ОТДЕЛЯЕТСЯ от
 * набора ИТ-службы, у которого держатели уже есть, и миграция разделения обязана выдать его
 * каждому из них в той же транзакции, где снимает права с ИТ-набора. Иначе разделение отобрало бы
 * у живых сотрудников работу исполнителя в момент выката — то есть сделало бы ровно то, чего
 * добивается обратное.
 */
export const MODULE_GRANTS: Record<ModuleGrantCode, CatalogGrant> = {
  /**
   * Номенклатура расходников — отдельным набором, а не строкой в «Оргтехника: ведение», по прямой
   * просьбе заказчика (Н12): справочник картриджей ведёт один человек, а заявки может вести
   * кто-то ещё. Держится это разделение двумя правами номенклатуры: по плану расходников она
   * правилась общим `officeEquipment.write`, и пока это так, набор не отделён ничем — выдав его,
   * выдали бы заодно ведение всего парка техники и моделей.
   *
   * Права два, а не одно, и разведены они намеренно (`permissions.ts`): `manage` — заведение,
   * правка, гашение и совместимость с моделями, `stock` — ручная корректировка остатка, то есть
   * единственная ручка, пишущая событие журнала от имени человека. В наборе они вместе: остаток
   * правит тот, кто ведёт позиции, — а разведённые по двум наборам они дали бы администратору
   * выбор, которого в работе не существует.
   *
   * Чтения в составе нет: карточка расходника открывается широким `officeEquipment.read`, и он же
   * объявлен требованием обоих прав (`PERMISSION_REQUIRES`). Держатель набора без чтения модуля
   * барьер выдачи не пройдёт.
   *
   * **Роли — бизнес-решение, а не следствие состава** (§7.2): номенклатуру ведут там же, где
   * оргтехнику, — на площадке и в отделе. Ролей с `officeEquipment.read` в портале больше
   * (менеджер, диспетчер, руководитель строительства, руководитель отдела, наблюдатель), и
   * требование чтения их не отсеивает — оно лишь проверяет, что каждая **перечисленная** ему
   * удовлетворяет: строка `grant_roles` под ролью без чтения была бы обещанием, которого проверка
   * выдачи не выполнит.
   */
  office_equipment_consumables: {
    name: 'Оргтехника: номенклатура',
    permissions: ['officeEquipmentConsumables.manage', 'officeEquipmentConsumables.stock'],
    roles: ['shtab', 'site', 'department'],
  },
  /**
   * Работа исполнителем в оргтехнике — та половина набора «Оргтехника: ИТ-служба», которая
   * чинит руками (план `docs/office-equipment-access-profiles-plan.md`, Р2 и Р4; план аудита
   * исполнителей, Н1 и Э8).
   *
   * **Зачем отделено.** До разделения `serviceRequests.execute` приходил ровно из одного набора —
   * ИТ-службы, — а у того сквозная область модуля (`GRANT_MODULE_WIDE_SCOPE`). Право исполнителя
   * поэтому было неотделимо от права видеть ВСЕ заявки компании: назначить сотрудника
   * исполнителем можно было только вместе со сквозной видимостью предмета, переписки и сумм по
   * любой чужой заявке. Разделение разносит координацию и работу руками на два набора; профиль
   * «Системный администратор» выдаётся обоими сразу и в правах не теряет ничего.
   *
   * **Сквозной области у набора нет и быть не может.** `GRANT_MODULE_WIDE_SCOPE` типизирован
   * `SystemGrantCode`, и модульный код туда не примет компилятор — это и есть защита решения, а не
   * комментарий о нём. Заявку держателю открывает третья ось видимости: «я назначен поимённо»
   * (план аудита, Р1), и работает она без всякого набора.
   *
   * **Права три, и каждое из них — своё.** `execute` открывает ходы исполнителя (взять в работу,
   * закрыть работы, вести примечание), но только на заявке, где держатель назначен; `files` —
   * подшить бумагу; `read` в составе потому, что набор самодостаточен (Р4): `PERMISSION_REQUIRES`
   * объявляет оба первых зависимыми от чтения, и без него набор обещал бы то, чего барьер выдачи
   * не пропустит роли без модуля. Действующим держателям чтение ничего не добавляет — все три
   * совместимые роли дают его сами как заказчику.
   *
   * **`serviceRequests.finance` здесь нет, и это решение, а не очередь** (план карточки заявителя,
   * Р2; миграция 0259 записала его заранее). Право сквозное по области: выданное набором, оно
   * показало бы держателю суммы всех заявок его площадки и отдела, а не тех, на которые он
   * назначен. Деньги назначенной заявки открывает та же ось назначения, что и ходы.
   *
   * **Роли — только те три, у которых есть своя ось.** `manager`/`dispatcher` сюда не годятся: при
   * `roleScopeAxis = 'none'` область заявок глобальна, и scope-neutral набор дал бы такому
   * сотруднику чтение всех заявок ещё ДО первого назначения — то есть ровно ту дыру, которую
   * разделение и закрывает. Заводить зависимость «executor только вместе с ИТ» сложнее и хрупче,
   * чем не обещать несуществующую область.
   */
  office_equipment_executor: {
    name: 'Оргтехника: исполнитель',
    permissions: ['serviceRequests.read', 'serviceRequests.execute', 'serviceRequests.files'],
    roles: ['shtab', 'site', 'department'],
  },
  /**
   * Заявитель оргтехники — **базовое состояние роли, завёрнутое в набор ради ролей без модуля**
   * (план профилей, Р6; этап Э7, миграция D).
   *
   * ЗАЧЕМ ОН, ЕСЛИ ЗАЯВИТЕЛЬ И ТАК ЕСТЬ. Для `shtab`, `site`, `department` (и упраздняемых
   * `rukstroy`/`department_head`) эти шесть прав даёт сама роль: заявку на свой принтер они заводят
   * без всякой выдачи. Роли без модуля — диспетчеру — их не даёт никто, а сломанный принтер стоит
   * ровно у него. Набор и есть ответ: он выдаётся поимённо, с автором и временем в реестре, и
   * снимается галочкой — в отличие от права, дописанного в роль, которое обратно забирается только
   * выкатом и достаётся всей диспетчерской разом.
   *
   * МЕНЕДЖЕР СТОЯЛ ЗДЕСЬ ВТОРЫМ ДО 10.09.2026: его круг переехал в саму роль (ADR 0181), и не
   * потому, что довод выше ослаб, а потому, что `manager` — роль временная, и раздача, пережившая
   * её удаление строкой в реестре выдач, была бы хуже раздачи, уходящей вместе с ней.
   *
   * СОСТАВ — РОВНО ТО, ЧТО ДЕЛАЕТ ЗАКАЗЧИК (Р4): прочитать справочник, чтобы выбрать аппарат;
   * прочитать, завести, поправить и удалить свою заявку; подшить к ней фотографию поломки.
   * Ни срочности, ни назначения, ни статусов, ни объёма работ, ни денег — на каждое из них у
   * профиля «Заявитель» стоит «нет» в матрице §5, и доказывают эти «нет» отрицательные тесты, а не
   * отсутствие права в этом списке. `serviceRequests.finance` здесь нет по той же причине, по
   * которой его нет у исполнителя: карточку заявителя от денег и отделяли.
   *
   * ЧТЕНИЕ У ЕГО ДЕРЖАТЕЛЯ ГЛОБАЛЬНО, И ЭТО ДЕЙСТВУЮЩАЯ МОДЕЛЬ, А НЕ ДЫРА. У ролей без оси
   * (`roleScopeAxis = 'none'`) предикат области не сужает ничего — так работает всякое их право во
   * всяком модуле. Изменяющие же действия сужает страж авторства (`actsAsRequestCustomer`, Р6):
   * держатель этого набора без оси правит, удаляет и подшивает только то, что завёл сам. Страж
   * выкачен той же волной, что и набор, и порядок здесь жёсткий — сперва предикат, потом каталог.
   *
   * РОЛЬ ОСТАЛАСЬ ОДНА — `dispatcher` (ADR 0181). Менеджер набор перерос: 10.09.2026 круг заявителя
   * выдан ему прямо матрицей роли, и с этого дня набор не добавляет его держателю ни одного права —
   * то есть попадает ровно под запрет теста каталога («набор обязан что-то давать каждой
   * перечисленной роли»). Строка `manager` здесь стала бы обещанием пустой выдачи: администратор
   * ставит галочку, реестр пишет автора и время, а прав не меняется ни одного.
   *
   * Диспетчер при этом остался на наборе, и асимметрия эта не забывчивость: у менеджера роль
   * временная и уйдёт целиком, а вместе с ней уйдёт и строка в матрице; у диспетчера роль
   * постоянная, и поимённая выдача с автором и временем — то, чем этот доступ и должен
   * оставаться (Р5).
   *
   * Ролям-заказчикам набор не нужен по той же причине, что и менеджеру: права у них уже есть.
   * `observer` только смотрит, `mechanic`/`chief_mechanic` ведут парк техники, `operator` (внешний
   * исполнитель) не перечисляется ни в одном наборе оргтехники никогда (Р11).
   *
   * САМОДОСТАТОЧЕН ПО ПОСТРОЕНИЮ (Р4): `PERMISSION_REQUIRES` объявляет `update`, `delete` и `files`
   * зависимыми от `serviceRequests.read`, и он в составе. Иначе барьер требований отклонил бы
   * выдачу ровно тем ролям, ради которых набор заведён, — у них прав модуля нет ни одного.
   */
  office_equipment_requester: {
    name: 'Оргтехника: заявитель',
    permissions: [
      'officeEquipment.read',
      'serviceRequests.read',
      'serviceRequests.create',
      'serviceRequests.update',
      'serviceRequests.delete',
      'serviceRequests.files',
      /*
       * Седьмое право — сообщение о технике, которой нет в справочнике (ADR 0165, план кандидатов
       * §14 M4а; план предмета заявки, этап Э6). Оно приезжает сюда по той же причине, по которой
       * заведён сам набор: сломанный принтер стоит у менеджера и диспетчера, и не найти его в
       * справочнике им случается чаще, чем площадке, — парк офиса как раз и размечен хуже всего.
       * Ролям-заказчикам то же право даёт матрица (`SERVICE_REQUEST_CUSTOMER_PERMISSIONS`), и
       * разойдись эти два источника — один и тот же тупик формы вёл бы себя по-разному в
       * зависимости от того, чем человеку выдали заявителя.
       *
       * САМОДОСТАТОЧНОСТЬ НЕ НАРУШЕНА: `PERMISSION_REQUIRES` требует для него `serviceRequests
       * .create` и `officeEquipment.read` — оба в составе, строками выше. Барьер выдачи поэтому
       * пропустит набор ровно тем двум ролям, ради которых он и заведён, — у них прав модуля нет
       * ни одного.
       *
       * ЧТО ЭТО НЕ ДАЁТ ДЕРЖАТЕЛЮ: ни очереди проверки, ни решений по сообщению
       * (`officeEquipment.review` живёт в «Ведении»), ни записи в справочник. Он сообщает —
       * карточку по сообщению заводит проверяющий.
       */
      'officeEquipment.propose',
    ],
    roles: ['dispatcher'],
  },
  /**
   * Аудит распознавания талонов вывоза — **одноцелевой набор с единственным правом** (план аудита
   * §4.1). Одно право здесь не признак недоделанного набора, а всё его содержание: аудит открывает
   * шесть читающих ручек и окно поверх реестра вывоза, и разрезать это на «смотрит сводку» и
   * «смотрит споры» значило бы предложить администратору выбор, которого в работе не существует.
   * Прецедент — «Аудитор» с одним `audit.read`.
   *
   * **Зачем набор, если право можно было бы выдать ролью.** Право сквозное: держатель видит адреса
   * всех площадок и сканы талонов всех исполнителей (§4.1). Раздай его матрица — сквозная картина
   * досталась бы каждому диспетчеру и каждому менеджеру разом, а заказчик просил обратного:
   * назвать человека. Набор — единственный способ выдать право поимённо, с автором, временем и
   * записью в реестре; прямой персональной выдачи модель ADR 0106 не знает вовсе, а
   * `NON_GRANTABLE_PERMISSIONS` дал бы ровно противоположное — право, которое не выдать никому.
   *
   * **Роли — `dispatcher` и `manager`, и выбор этот не про должность, а про две вещи сразу.**
   *
   * Первая — работа. Аудит меряет качество машинного чтения теми правками, которые делает человек
   * при разборе талонов; разбор ведёт диспетчерская (`WASTE_REQUEST_PERMISSIONS` —
   * `wasteRequests.ticketReview` там), и она же отвечает за то, чтобы распознавание настраивали по
   * фактам, а не по ощущению. Смотреть на свои же исправления со стороны — её работа, а не чужая.
   *
   * Вторая — область, и она жёстче первой. Аудит **не фильтруется** предикатами вывоза: журнал
   * наблюдений живёт своей таблицей и переживает и талон, и заявку (§4.2). Матрица осей этого не
   * выражает и выразить не может: клетка `waste × object` — `scoped`, и говорит она за
   * `placeObjectVisibilityWhere`, то есть за заявки, а не за журнал их разбора. Значит набор
   * обязан уходить только ролям без своей оси, у которых обещание «увидит своё» ничего не
   * обещает, — иначе площадка получила бы сквозную картину, а барьер выдачи промолчал бы, потому
   * что смотрит на модуль, а не на право. Из ролей без оси диспетчер и менеджер — единственные,
   * кто с вывозом работает: наблюдатель по построению только смотрит и решений о качестве не
   * принимает, обе роли ОГМ ведут парк и вывоза не касаются (`wasteRequests.read` у них нет
   * вовсе — барьер требований отклонил бы выдачу и без этой строки).
   *
   * **`admin` в ролях нет намеренно**, и причина не в осторожности: право у него уже есть от
   * матрицы (`admin: [...PERMISSIONS]`), и строка `grant_roles` под ним означала бы набор, выдача
   * которого не меняет ничего, — то самое, что ловит тест «набор обязан что-то давать». `driver`
   * нет по общему правилу: наборов эта роль не принимает ни одним способом (барьер 2).
   *
   * **Никому не выдан.** Миграция каталога заводит набор, состав и совместимые роли — и на этом
   * заканчивается: `user_grants` не трогает ни одной строкой.
   */
  waste_ticket_audit: {
    name: 'Вывоз мусора: аудит распознавания',
    permissions: ['wasteRequests.ticketAudit'],
    roles: ['dispatcher', 'manager'],
  },
  /**
   * Разбор карантина файлов (план освобождения от подписи, Р6, п. 4) — одноцелевой набор из одного
   * права, как «Аудитор» и аудит талонов.
   *
   * **Зачем набор, если право можно было бы выдать ролью.** Карантинный файл открыт держателю
   * поперёк всех областей: файл приходит из любого модуля, и сужать разбор инцидента площадкой
   * значило бы оставить без разбора ровно тот случай, ради которого карантин и заведён — документ
   * попал в чужую карточку. Раздай такое право матрица — содержимое любого запертого документа
   * досталось бы всей диспетчерской разом; работу ведёт названный человек, и называет его
   * администратор строкой с автором и временем.
   *
   * **Роли — `dispatcher` и `manager`, и выбор про область, а не про должность.** Клетка
   * `files × object|department|counterparty` — `forbidden` (`GRANT_SCOPE_MATRIX`): предиката
   * области у файлов нет ни одного, и набор с правом этого модуля, выданный роли с осью, отдал бы
   * ей документы всей компании. Из ролей без оси отпадают водитель (наборов не принимает вовсе),
   * наблюдатель (по построению ничего не решает) и обе роли службы механика (инциденты с
   * персональными данными разбирает не ОГМ). Остаются две, и обе уже удаляют ничейные чужие файлы
   * правом `files.manageAny` — то есть отвечают за файловый слой и без этого набора.
   *
   * **`admin` в ролях нет намеренно**: право у него уже есть от матрицы (`admin: [...PERMISSIONS]`),
   * и строка `grant_roles` под ним означала бы набор, выдача которого не меняет ничего.
   *
   * **Никому не выдан.** Миграция `0308_files_quarantine_grant.sql` заводит набор, состав и
   * совместимые роли и не создаёт ни одной строки `user_grants`.
   */
  files_quarantine_audit: {
    name: 'Файлы: разбор карантина',
    permissions: ['files.quarantineAudit'],
    roles: ['dispatcher', 'manager'],
  },
  /**
   * Журнал путевых листов площадке и отделу — просмотр, печать, выгрузка бланка и подшитые к листу
   * сканы (ADR 0192, решение Р4). Одно право: `waybills.read` открывает в модуле ровно это, а
   * подшивка (`waybills.files`), списание номера (`waybills.cancel`), пустой бланк и коррекция
   * задним числом остаются диспетчерской — заказчик просил видеть бумагу, а не вести её.
   *
   * **ЭТОТ НАБОР — ЕДИНСТВЕННЫЙ В КАТАЛОГЕ, КОТОРЫЙ ВЫДАЁТСЯ РОЛИ С ОСЬЮ ОБЛАСТИ.** Возможным его
   * сделал не пересмотр правила, а работа, проделанная до него: у модуля появился предикат
   * (`waybillVisibilityWhere` в `lib/access.ts`), и клетки `waybills × object|department` в
   * `GRANT_SCOPE_MATRIX` сменились с `forbidden` на `scoped` вслед за ним. Барьер выдачи считает
   * матрицу, а не этот комментарий: убери предикат — и клетка обязана вернуться в `forbidden`,
   * после чего выдача набора перестанет проходить сама.
   *
   * **Персональные данные водителя уходят вместе с бумагой, и это объявленное расширение.** В
   * бланке печатаются СНИЛС и номер удостоверения (ADR 0037), а решение реформы доступа от
   * 18.08.2026 говорило «ПДн водителей вне объектных ролей». ADR 0192 отменяет тот пункт прямо:
   * площадка получает путевой лист на бумаге, потому что работает по нему на объекте. Отменяет
   * ровно в этой части — карточки водителей (`drivers.read`) и парк площадке по-прежнему закрыты,
   * и наборов под них в каталоге нет.
   *
   * **Роли — шесть, и три из них упраздняемые.** `site`, `department`, `department_head` — целевые;
   * `shtab`, `rukstroy`, `commandant` — живые сегодня, и без них набор было бы некому выдать: роль
   * `site` на момент выката не назначена ни одной учётке, перевод идёт отдельными этапами 8 и 9.
   * Их строки `grant_roles` уйдут вместе с самими ролями — `DELETE FROM grant_roles WHERE role IN
   * (...)` в `pending/stage8-cleanup-site-role.sql`, — и тем же релизом эти три имени обязаны уйти
   * отсюда: страж каталога сверяет обе стороны, и разъехавшись, они покраснеют на cleanup'е.
   * `manager`, `dispatcher` и обе роли ОГМ в списке отсутствуют не по недосмотру — право у них уже
   * есть от матрицы, и набор ничего бы им не дал.
   *
   * **Никому не выдан.** Миграция каталога заводит набор, состав и совместимые роли; кому его
   * поставить галочкой в окне учётки — решает администратор портала поимённо.
   */
  waybills_view: {
    name: 'Путевые листы: просмотр и печать',
    permissions: ['waybills.read'],
    roles: ['site', 'department', 'department_head', 'shtab', 'rukstroy', 'commandant'],
  },
};

/**
 * Все системные коды каталога — перенесённые из надстроек, административные, ролевые и модульные.
 *
 * Нужен там, где вопрос звучит «системный ли это набор», а не «откуда он приехал»: страж каталога
 * сверяет по нему обе стороны (в базе нет лишних системных наборов, в коде — нет незаведённых), а
 * `grantCodeSchema` не отдаёт ни один из них администратору под пользовательский набор. Разъехаться
 * списки не могут: этот собран из тех четырёх, а не переписан пятым перечнем.
 */
export const ALL_SYSTEM_GRANT_CODES = [
  ...SYSTEM_GRANT_CODES,
  ...ADMIN_GRANT_CODES,
  ...ROLE_GRANT_CODES,
  ...MODULE_GRANT_CODES,
] as const;

/**
 * Права, которые не попадают ни в один назначаемый набор — ни в системный, ни в собранный
 * администратором, ни в будущую точечную выдачу (ADR 0106, решение 6, инвариант 1).
 *
 * Шесть, и каждое стоит здесь по своей причине:
 *
 * - `records.purge` — удаление записи насовсем, мимо пометки «удалена»: восстановить её уже нечем,
 *   и ошибка держателя набора не лечится ничем, кроме резервной копии;
 * - `users.manage` — ведение учёток, то есть выдача доступов. Право, которым набирают права,
 *   собранное набором, замыкает модель саму на себя: держатель дособирает себе что угодно, и
 *   каталог полномочий перестаёт быть границей;
 * - `waybills.correctBeyondLimit` — правка документов глубже тридцати дней. За пределом начинается
 *   разрыв хронологии журнала БСО, и отвечает за него тот, кто отвечает за журнал (ADR 0101, Р37);
 * - `driverCabinet.read` и `driverCabinet.submit` — **оба** права кабинета. Кабинет показывает
 *   задание конкретного работника и принимает показания от его имени (ADR 0102); достаться
 *   «заодно с пакетом» он не должен ни на чтение, ни на запись;
 * - `autoParts.delete` — удаление чека на автозапчасти (план `docs/auto-part-receipts-plan.md`,
 *   Р4а), шестое и по той же причине, что первое: запись уходит насовсем вместе со строками и
 *   отвязанными сканами, восстановить документ нечем. Требование заказчика — «удаляет только
 *   администратор», и роли право не выдано ни одной; но состав набора произволен, и без этой
 *   строки администратор собрал бы право в назначаемое полномочие, а «только администратор»
 *   держалось бы на том, что никто такого набора не собрал, — то есть ни на чём. Пометку на
 *   удаление ставит и снимает `autoParts.manage`, и она назначаемая: помечать чек — работа
 *   механика, уничтожать — нет.
 *
 * Назначаемых прав поэтому 70 из 76 — в ADR 0106 было 50 из 55, и расходятся числа ровно так, как
 * задумано: словарь с тех пор вырос в чистом остатке на двадцать одно право — пришло на одно
 * больше, но `autoParts.stock` успело и уйти, вместе со складом, который оно охраняло, — и двадцать
 * из них стали назначаемыми сами (руководства, например, ведёт тот, кто их пишет, а не обязательно
 * администратор; `serviceRequests.finance` — тот, кто отвечает за деньги модуля), а двадцать
 * первое — `autoParts.delete` — приехало сразу в запрет и потому пополнило только правое число
 * (всего прав), не тронув левое (назначаемых). Берётся число из константы, а не из текста:
 * реализация, пошедшая за «всё, кроме тройки административных прав», выдала бы кабинет чужой роли
 * — то есть чужие персональные данные — и сделала бы это молча, пройдя все тесты, написанные по
 * той же тройке.
 *
 * Список обязан быть один на API, интерфейс и тесты. Три копии расходятся не одновременно: сначала
 * форма покажет чекбокс, который сервер отклонит (пользователь увидит непонятный отказ), потом
 * сервер примет то, чего форма не показывает (доступ, которого никто не собирал), а тест, живущий
 * по третьей копии, подтвердит и то и другое. Поэтому константа лежит в контрактах — там же, где
 * сам словарь прав.
 */
export const NON_GRANTABLE_PERMISSIONS = [
  'records.purge',
  'users.manage',
  'waybills.correctBeyondLimit',
  'driverCabinet.read',
  'driverCabinet.submit',
  'autoParts.delete',
] as const satisfies readonly Permission[];

const NON_GRANTABLE_SET: ReadonlySet<Permission> = new Set(NON_GRANTABLE_PERMISSIONS);

/**
 * Можно ли включить право в назначаемое полномочие. Предикат, а не проверка `includes` по месту:
 * спрашивают его схема запроса, конструктор и тесты, и все трое обязаны получить один ответ.
 */
export function isGrantable(permission: Permission): boolean {
  return !NON_GRANTABLE_SET.has(permission);
}

/**
 * Что должно быть у субъекта, чтобы право имело смысл (ADR 0106, решение 6, инвариант 4).
 *
 * Инвариант ADR 0021 «модуль закрывается чтением» свободная сборка ломает первым: набор из одного
 * `vehicleRequests.status`, выданный коменданту, открывает прямой вызов смены статуса, хотя
 * `vehicleRequests.read` у него нет и раздела он не видит. Отсюда проверка после каждой операции,
 * меняющей доступ: для каждого эффективного права субъекта `PERMISSION_REQUIRES[право]` обязано
 * быть подмножеством его эффективных прав.
 *
 * Проверка идёт по **итоговым правам учётки**, а не внутри набора, и системный «Оператор
 * (оргтехника)» — прямое тому доказательство: `officeEquipment.write` даёт он, а
 * `officeEquipment.read` приходит от базовой роли. Набор сам по себе инвариант нарушает, субъект —
 * нет.
 *
 * **Витринный `MODULE_ENTRY_PERMISSION` границей безопасности служить не может.** Он группирует
 * права для экрана: у модуля `admin` входное право — `users.manage`, то есть ровно то, которое
 * защищено `NON_GRANTABLE_PERMISSIONS`. Проверка «по модулю» потребовала бы его от любого набора с
 * правами этого модуля и отклонила бы будущих «Аудитора» и «Рассылки» целиком — наборы, ради
 * которых реформа и затевалась. Альтернатива «разделить `admin` на три модуля витрины» рабочая, но
 * она чинит витрину ради безопасности, а не наоборот; поэтому требования задаются своей таблицей и
 * по праву, а не по модулю.
 *
 * Строка «требует само себя» проверкой ничего не делает — подмножество из самого права выполняется
 * всегда. Она объявляет, что право **входное**: за ним не стоит ничего, и пустота здесь — решение,
 * а не забытая строка. Ради этого различия таблица и хранит такие строки явно.
 *
 * **Тип — `Partial`, а не полный `Record`, и это осознанный отказ от помощи компилятора.** Полный
 * `Record` заставил бы приписать `[]` сорока двум правам, и каждая такая строка утверждала бы то,
 * чего решение не утверждало: «это право самодостаточно, его можно выдать в одиночку». Первая же
 * из них — `wasteRequests.create: []` — прямо противоречила бы ADR 0021, где модуль закрывается
 * чтением. Расписать же честные требования всем действиям всех модулей значит принять сейчас
 * решения, которых в ADR 0106 нет, и сделать это в файле, который на этапе 1a обязан только
 * заводить структуры. Поэтому здесь только объявленные требования (число в комментарии не держим — оно
 * устаревало при каждом новом праве), а отсутствие
 * строки читается как «требование не объявлено», а не как «право ничего не требует»; расширяет
 * таблицу решение, а не молчание. Читают её через `?? []`.
 */
export const PERMISSION_REQUIRES: Partial<Record<Permission, readonly Permission[]>> = {
  // Входные права трёх административных наборов, ради которых заведена таблица: журнал действий и
  // рассылки открываются своим чтением, а не общим `users.manage` модуля витрины.
  'audit.read': ['audit.read'],
  'mailings.read': ['mailings.read'],
  'mailings.manage': ['mailings.read'],
  'users.manage': ['users.manage'],
  /*
   * Ведение руководств — тоже входное право, и объявлено оно самоссылкой по той же причине, что
   * `audit.read`: за ним не стоит ничего, читать список руководств разрешено всем вошедшим, а
   * пустая строка в этой таблице читается как «требований не объявили», а не как «право
   * самодостаточно» (`docs/manuals-plan.md` §3.1). Модуль витрины у права свой (`manuals`), и
   * входное право модуля совпадает с ним самим — конструктору нечего требовать сверх.
   */
  'manuals.manage': ['manuals.manage'],
  /*
   * Разбор карантина файлов (план освобождения от подписи, Р6, п. 4) — право ВХОДНОЕ, и самоссылка
   * здесь объявляет это явно, как у `audit.read`: пустая строка читалась бы «требований не
   * объявили».
   *
   * Требовать `files.manageAny` нельзя, хотя витрина считает его входным правом модуля `files`:
   * то право про удаление ничейного файла, и с разбором инцидента у него общего ровно столько же,
   * сколько у `users.manage` с чтением журнала действий. Потребуй мы его — набор «Файлы: разбор
   * карантина» нельзя было бы выдать никому, кроме тех, кому уже разрешено удалять чужие файлы, то
   * есть разбор инцидента стал бы следствием права на удаление.
   *
   * Чтения модуля за правом не стоит и по существу: карантинный файл открывается САМИМ этим
   * правом, раньше любой ветки видимости (`canAccessFile`), и никакой модуль для этого не нужен.
   */
  'files.quarantineAudit': ['files.quarantineAudit'],
  /*
   * Ведение справочника оргтехники (`docs/office-equipment-access-profiles-plan.md`, Р12). Причина
   * та же, что у номенклатуры и закупки ниже, только про сам парк: карточка аппарата открывается
   * `officeEquipment.read`, и набор с одним `write`, выданный роли без чтения, дал бы право
   * заводить, править и списывать единицы, ни одной из которых держатель не видит ни в одном
   * списке портала.
   *
   * Строки не было до сих пор потому, что право приходило одной надстройкой «Оператор
   * (оргтехника)», а её базовые роли (`ROLE_ADDON_BASE_ROLES`: штаб, площадка, отдел) читают
   * справочник по построению, — ставит вопрос впервые свободная сборка. Держателям надстройки
   * строка ничего не меняет и **набор из-за неё не становится невыдаваемым**: проверка идёт по
   * итоговым правам субъекта, и «Оператор (оргтехника)» — тот самый пример из преамбулы, где
   * состав набора инвариант нарушает, а субъект — нет.
   */
  /*
   * The readings report of the garage header and «Выгрузки» (ADR 0240, ADR 0241). Its rows
   * are vehicle requests by day: request number, customer, approver, cargo, object and route. So
   * it requires reading both modules it carries: a grant with the export alone would hand the
   * whole fleet's readings, and every request of the period, to someone shown none of them on any
   * screen. The route also refuses a principal with a narrow area (`assertWholeOrganizationScope`):
   * the report is company-wide by construction, and cutting it by area is not what it is for.
   * The roles that hold the right by their sets carry both reads with it (ADR 0241).
   */
  'vehicleReadings.export': ['vehicleReadings.read', 'vehicleRequests.read'],
  /*
   * У `analytics.export` требований нет, и его отсутствие здесь — решение, а не упущение (план,
   * Р2): книга сводит три модуля, требование читать все три закрыло бы её целевому держателю, а
   * персональных данных в ней нет вовсе.
   */
  'officeEquipment.write': ['officeEquipment.read'],
  /*
   * Подтверждение перемещения (план `docs/office-equipment-move-from-request-plan.md`, Р1).
   * Требование ОДНО, и `officeEquipment.write` в нём намеренно нет: в этом весь смысл права.
   * Целевой его держатель — ИТ-служба, у которой ведения справочника нет и не появляется, и
   * потребуй мы `write`, круг держателей остался бы прежним, то есть право не сузило бы ничего.
   *
   * Зато чтение справочника требуется: перемещение подтверждают ГЛАЗАМИ — человек видит, где
   * аппарат числится, и говорит, куда он переехал (сверка `from`, Р3). Набор с одним `move`,
   * выданный роли без чтения парка, дал бы право переносить единицы, ни одной из которых держатель
   * не видит ни в одном списке портала, — тот же случай коменданта, ради которого таблица заведена.
   */
  'officeEquipment.move': ['officeEquipment.read'],
  // Разбор очереди писем без чтения справочника — дверь в пустоту: держатель увидел бы очередь, в
  // которой не может подобрать карточку для привязки (Р30).
  'officeEquipment.telemetry': ['officeEquipment.read'],
  /*
   * Reporting equipment missing from the directory (candidates plan, Р8): a row with TWO
   * requirements (like `vehicleReadings.export` above), and neither is a formality.
   *
   * `serviceRequests.create`: a candidate does not exist apart from a request — it explains why the
   * request has no subject and arrives with the same `POST /service-requests`. The right without
   * creating would promise what the server refuses, the very mistake the first edition of the
   * purchasing plan made.
   *
   * `officeEquipment.read`: «not found in the directory» is a claim of someone who could search.
   * Without reading the fleet a person sees neither the equipment picker nor the types, and a grant
   * assembled by an administrator would open an entrance producing duplicates by construction:
   * absence would be reported by someone not shown what exists.
   */
  'officeEquipment.propose': ['serviceRequests.create', 'officeEquipment.read'],
  /*
   * Проверка сообщений (план кандидатов, Р8). Требование одно, зато оно и есть замок: подтверждение
   * ЗАВОДИТ карточку парка, и право проверки, выданное без `officeEquipment.write`, стало бы вторым
   * входом в справочник мимо того права, которым этот вход охраняется. Проверяется по итогу учётки,
   * поэтому набор «Ведение» ему удовлетворяет сам, а собранный администратором — только вместе с
   * ведением справочника.
   */
  'officeEquipment.review': ['officeEquipment.write'],
  /*
   * Номенклатура расходников (план расходников, Р10). Оба права требуют чтения справочника
   * оргтехники, и требование здесь не формальность: карточка расходника открывается тем же
   * `officeEquipment.read`, и набор с одним `…manage`, выданный роли без чтения, дал бы человеку
   * право править то, чего он не видит.
   */
  'officeEquipmentConsumables.manage': ['officeEquipment.read'],
  'officeEquipmentConsumables.stock': ['officeEquipment.read'],
  /*
   * Плановая закупка расходников (план расходников и закупки, Р12) — то же требование и по той же
   * причине, что у номенклатуры, только острее: форма закупки собирается из позиций справочника —
   * код, наименование, остаток, потребность, — и показывает их прямо в строках документа. Набор с
   * одной закупкой, выданный роли без чтения справочника, дал бы человеку право заказывать то,
   * чего он не видит ни в одном списке портала.
   */
  'officeEquipmentPurchases.manage': ['officeEquipment.read'],
  /*
   * Заявки оргтехники (план переделки цикла, §7.1; `docs/office-equipment-access-profiles-plan.md`,
   * Р12). Правило одно на все одиннадцать строк: действие над заявкой без её чтения — действие
   * вслепую, тот же случай коменданта, ради которого таблица и заведена.
   *
   * Тяжелее прочих `execute`: назначение открывает ходы заявки, и набор с одним этим правом,
   * выданный роли без чтения модуля, означал бы человека, которого можно назначить на заявку,
   * невидимую ему самому, — он не увидит ни задания, ни того, что с ним стало. До каталога вопрос
   * не стоял: права модуля выдавались ролью и надстройкой, где чтение есть по построению, — ставит
   * его впервые свободная сборка.
   *
   * **Восемь последних строк списка ниже дописаны разом, по модулю целиком** (Р12, находка Н2), и
   * это не аккуратизм.
   * Таблица знала четыре права модуля из четырнадцати: назначение, ход цикла, обе визы, смета,
   * правка, удаление и вложения не требовали ничего. Безвредно это ровно до тех пор, пока наборы
   * модуля выдаются `shtab`/`site`/`department`, у которых чтение есть по матрице; вредным
   * становится в первый же день, когда тот же набор предложат роли без модуля — а такой день
   * назначен планом (расширение `grant_roles` на `manager` и `dispatcher`). Дописывать строки по
   * одной, вслед за ролями, значит каждый раз решать заново уже решённый вопрос — и однажды решить
   * его не глядя, потому что «здесь ведь и так у всех чтение есть».
   *
   * Ещё одна строка модуля — `serviceRequests.finance` — приехала **не этим этапом**, и стоит она
   * ниже, со своим обоснованием. Э2 её не писал намеренно: права тогда не было в `PERMISSIONS`,
   * а таблица объявлена `Partial<Record<Permission, …>>` — ключа, которого словарь не знает,
   * компилятор не примет, и дописать требование «на будущее» здесь физически нельзя. Это и есть
   * правило, которым живёт таблица: **требование едет вместе с правом**, в том же плане и тем же
   * выпуском, что и само право (`docs/office-equipment-requester-card-plan.md`).
   *
   * Живым учёткам строки ничего не меняют: проверка идёт по итоговым правам субъекта, а чтение
   * модуля есть у каждого сегодняшнего держателя — и у ролей-заказчиков, и у сервисной компании
   * (`COUNTERPARTY_TYPE_PERMISSIONS.service`), и у обеих надстроек через базовую роль. Закреплено
   * это не словами, а стражем: каждый субъект `ACCESS_PROFILES` перебором сверяется с этой таблицей
   * (`apps/api/test/grants-contracts.test.ts`).
   */
  'serviceRequests.execute': ['serviceRequests.read'],
  'serviceRequests.hold': ['serviceRequests.read'],
  // Cancel (workflow-fixes plan Р2): cancelling a request one cannot see is acting blind.
  'serviceRequests.cancel': ['serviceRequests.read'],
  'serviceRequests.urgency': ['serviceRequests.read'],
  'serviceRequests.assign': ['serviceRequests.read'],
  'serviceRequests.status': ['serviceRequests.read'],
  'serviceRequests.approveEstimate': ['serviceRequests.read'],
  'serviceRequests.estimate': ['serviceRequests.read'],
  /*
   * Раскладка объёма работ по графам (план
   * `docs/office-equipment-free-estimate-and-executor-scope-plan.md`, Р2) — то же требование, что у
   * одиннадцати соседей по модулю, и по той же причине: переписать объём работ заявки, которой не
   * видно, — действие вслепую. Держателю набора «Ведение» строка ничего не меняет: чтение модуля
   * стоит в его составе (`ROLE_ADDON_PERMISSIONS`), и барьер выдачи он проходит сам.
   */
  'serviceRequests.estimateRewrite': ['serviceRequests.read'],
  'serviceRequests.approveIt': ['serviceRequests.read'],
  'serviceRequests.update': ['serviceRequests.read'],
  'serviceRequests.delete': ['serviceRequests.read'],
  'serviceRequests.files': ['serviceRequests.read'],
  /*
   * Деньги и объём работ заявок (план `docs/office-equipment-requester-card-plan.md`, Р1) — та же
   * строка, но случай у неё свой и мягче соседних. Право не открывает ручки вовсе: оно расширяет
   * ответ тех, что открыты чтением, — значит выданное без чтения оно не «действие вслепую», а
   * прибавка к выдаче, которой не будет ни одной. Набор с одним `finance` выглядел бы в реестре
   * как доступ к суммам компании, а не давал бы ничего; требование превращает это в внятный отказ
   * при выдаче.
   *
   * Живым учёткам строка ничего не меняет: право приходит двумя наборами оргтехники и типом
   * контрагента `service`, и чтение модуля есть у каждого держателя по построению.
   */
  'serviceRequests.finance': ['serviceRequests.read'],
  /*
   * Заявка без аппарата (план расходников и закупки, Р5; ADR 0146, решение 6) требует ЗАВЕДЕНИЯ, а
   * не чтения, и это единственная строка модуля, устроенная так. Причина в том, что право не
   * открывает своей двери вовсе: ручка у него чужая — `POST /service-requests`, — и её статический
   * страж спрашивает `serviceRequests.create`. Держатель набора без заведения получил бы право,
   * которое сервер не пустит ни разу: не «действие вслепую», как у соседей выше, а обещание,
   * невыполнимое по построению.
   *
   * Чтения сверх этого не объявлено намеренно: `serviceRequests.create` само по себе выдаётся без
   * `read` и сегодня (`SERVICE_REQUEST_CUSTOMER_PERMISSIONS` даёт их вместе, но таблица требований
   * об этом не высказывалась), и приписать чтение здесь значило бы потребовать от заведения без
   * аппарата больше, чем от обычного заведения. Строгость не там, где решение её ставило.
   *
   * Живым учёткам строка ничего не меняет: право приходит двумя наборами оргтехники, базовые роли
   * которых (штаб, площадка, отдел) заводят заявки как заказчики.
   */
  'serviceRequests.createWithoutEquipment': ['serviceRequests.create'],
  /*
   * Разбор талонов вывоза (ADR 0114, Р25) без чтения заявок — то же действие вслепую: ручки
   * разбора вложены в заявку, а реестр «требующих разбора» это фильтр её списка, и без
   * `wasteRequests.read` открыть нечего ни там, ни там. Набор с одним разбором, выданный роли без
   * модуля, дал бы человека, который вправе снимать замечания к бумаге, которой не видит.
   */
  'wasteRequests.ticketReview': ['wasteRequests.read'],
  /*
   * Аудит распознавания (ADR 0137, план аудита §4.1) — та же пара, что у разбора, но причина у
   * неё другая, и она **не про происхождение данных**. Наблюдения аудит читает свои: журнал живёт
   * отдельной таблицей и переживает и талон, и заявку (§4.2), так что «без чтения заявок
   * показывать нечего» здесь было бы неправдой.
   *
   * Причина — про работу держателя и про то, чем закрыты ручки. Точка входа у аудита одна: кнопка
   * в панели модуля вывоза (§5), и без `wasteRequests.read` держатель модуля не видит вовсе —
   * нажимать нечего. Но дело не только в кнопке, иначе строка была бы косметикой: шесть ручек
   * аудита закрыты одним этим правом и отвечают на прямой вызов. Набор, выданный роли без вывоза,
   * отдал бы ей адреса всех площадок и сканы всех исполнителей — модулем, которого у неё нет. Это
   * случай коменданта, ради которого таблица и заведена, только в сквозном варианте: там открыт
   * ход по заявке, которой не видно, здесь — картина по компании, которой не видно тем более.
   *
   * Держателям набора `waste_ticket_audit` (диспетчер и менеджер) строка ничего не меняет: вывоз
   * у обоих есть по построению. Нужна она свободной сборке — и переезду: §5 называет размещение
   * окна временным, и переедет аудит в своё место — строку пересматривают вместе с переездом, а
   * не молча оставляют.
   */
  'wasteRequests.ticketAudit': ['wasteRequests.read'],
  // Списание испорченного бланка без журнала — действие вслепую: аннулировать нечего, если листов
  // не видно (ADR 0037).
  'waybills.cancel': ['waybills.read'],
  /*
   * Пустой бланк выписывается из рейса, а ручки рейсов стоят за парой прав `waybills.read` +
   * `vehicleRequests.status`. Набор с одним `waybills.issueBlank`, выданный роли без журнала, был бы
   * пустышкой — не утечкой, но обещанием, которого интерфейс не выполнит. Требование объявлено
   * поэтому: барьер отвечает «так набор не соберётся», а не оставляет это выясняться в бою.
   */
  'waybills.issueBlank': ['waybills.read'],
  // Ход заказа без его чтения — тот самый случай коменданта: статус меняется прямым вызовом, а
  // раздела у субъекта нет.
  'vehicleRequests.status': ['vehicleRequests.read'],
  /*
   * Механизация (план `docs/mechanization-module-plan.md`, Р9) — тот же случай коменданта, дважды.
   * Ход аренды без её чтения — договорённость и отметка выдачи по заявке, которой держатель не
   * видит; продление — согласие платить дальше за то же невидимое. Живым учёткам строки ничего не
   * меняют: у менеджера и диспетчера чтение модуля есть по построению — нужны они свободной
   * сборке, где право выдаётся в одиночку.
   */
  'mechRequests.status': ['mechRequests.read'],
  'mechRequests.extend': ['mechRequests.read'],
  /*
   * Виза аренды — третий случай того же рода и самый строгий (план визы, Р1, Р2): подпись под
   * заявкой, которой подписывающий не видит, — это не «неполный доступ», а согласие вслепую, а
   * заодно и тупик интерфейса: визировать нечего, потому что список пуст.
   *
   * Строка держит и главное следствие плана: после того как модуль снят у роли `site`, «Виза
   * объекта» перестаёт выдаваться в одиночку — вместе с ней держателю нужен «Заказ механизации».
   * Это буквальный смысл ответа заказчика «право визы у площадки одно на портал»: подписывают то,
   * что видят.
   */
  'mechRequests.approve': ['mechRequests.read'],
  /*
   * Обе визы — тот же случай, что ход заказа, и объявлены они этапом 4б вместе с набором «Виза
   * объекта» (ADR 0112). До каталога вопрос не стоял: право визы выдавалось только ролью, и обе
   * роли-держателя — `rukstroy` и `department_head` — читают свой модуль по построению. Свободная
   * сборка ставит его впервые: «Виза объекта», выданная площадке без «Заказа техники», означала бы
   * подпись под заявкой, которой подписывающий не видит, а виза недели к тому же необратима — она
   * той же транзакцией двигает сроки заказов.
   *
   * Живым учёткам строки ничего не меняют: проверка идёт по итоговым правам субъекта, а у каждого
   * сегодняшнего держателя визы чтение своего модуля уже есть (`ROLE_PERMISSIONS`, закреплено
   * тестом).
   */
  'vehicleRequests.approve': ['vehicleRequests.read'],
  'weeklyRequests.approve': ['weeklyRequests.read'],
  // Ведение журнала ТО без него самого — та же слепая правка, что аннулирование бланка без журнала
  // листов: акт заводят, видя предыдущий (план «Показания техники», Р14). Матрица ролей эту пару и
  // так не разводит — строка нужна свободной сборке, где право можно выдать в одиночку.
  'vehicleMaintenance.write': ['vehicleMaintenance.read'],
  /*
   * Автозапчасти (план автозапчастей, Р10; план чеков, Р4). Оба права модуля требуют чтения
   * гаража, и требование здесь не формальность: своего чтения у модуля нет вовсе — список чеков,
   * карточку и окно машины открывает `garage.read`, — поэтому набор с одним `autoParts.manage`,
   * выданный роли без гаража, дал бы человеку право вести то, чего он не видит. Строк было три,
   * пока у модуля был склад; `autoParts.stock` ушло из словаря вместе с ним (план чеков, Р4),
   * ушла и его строка. Ролям-держателям (`mechanic`, `chief_mechanic`) строки ничего не меняют:
   * гараж у обеих есть по построению; нужны они свободной сборке, где право выдаётся в одиночку.
   *
   * Вторая строка — `autoParts.delete` (план чеков, Р4а) — стоит здесь при том, что право
   * невыдаваемо (`NON_GRANTABLE_PERMISSIONS`), и это не противоречие. Проверка идёт по **итоговым**
   * правам учётки, а не по составу набора: сегодня она означает «у держателя удаления обязан быть
   * гараж», и у единственного держателя — администратора — он есть по построению. Объявлено
   * требование затем, чтобы правило приехало вместе с правом, а не вслед за ним: право уничтожать
   * то, чего не видишь, — та же дыра, что и право править невидимое, и первый же день, когда
   * удаление откроют кому-то ещё, застал бы таблицу без этой строки.
   */
  'autoParts.manage': ['garage.read'],
  'autoParts.delete': ['garage.read'],
};

/**
 * Пара прав, которую нельзя свести в одном субъекте, — разделение обязанностей (ADR 0106,
 * решение 6, инвариант 3). Причина хранится рядом с парой: отказ обязан называть виновников и
 * объяснять запрет теми же словами на API и в конструкторе, а не двумя разными.
 */
export interface GrantConflict {
  readonly permissions: readonly [Permission, Permission];
  readonly reason: string;
}

/**
 * Конфликты обязанностей первой поставки. Таблица, а не пример в тексте и не проверка по месту:
 * следующая пара добавляется строкой здесь, и её увидит всякий, кто читает границы сборки.
 *
 * Проверяется таблица по **итоговым эффективным правам учётки**, а не внутри набора: составление
 * сметы может лежать в одном полномочии, её утверждение — в другом, и по отдельности оба
 * безупречны. Исключение из проверки одно и привязано к роли `admin` (либо к невыдаваемому
 * признаку администратора), а не к объёму прав субъекта: правило «у кого есть весь модуль — того не
 * проверяем» отключало бы себя ровно в той операции, которая дособирает модуль и создаёт
 * запрещённую пару.
 */
export const GRANT_CONFLICTS: readonly GrantConflict[] = [
  {
    // Кто считает смету, тот её не утверждает: слитые в одном человеке, эти два права превращают
    // согласование расходов в подпись под собственной работой (ADR 0085).
    permissions: ['serviceRequests.estimate', 'serviceRequests.approveEstimate'],
    reason: 'Смету составляет и согласует один человек',
  },
  {
    // Виза ИТ отвечает на «зовём ли сервис вообще», и подтверждать необходимость работы, которую
    // сам же продаёшь, — не согласование. Запрет уже действует по ADR 0096; таблица повторяет его
    // там, где выполняется проверка, чтобы он не держался на одном лишь составе ролей.
    permissions: ['serviceRequests.estimate', 'serviceRequests.approveIt'],
    reason: 'Исполнитель сметы визирует необходимость собственной работы',
  },
  {
    /*
     * Кто делает работу, тот её себе не распределяет (план профилей оргтехники, Р11, п. 1).
     *
     * ЗАЧЕМ ТРЕТЬЯ ПАРА, КОГДА ЕСТЬ БАРЬЕР ОСЕЙ. Барьер 3 отбивает у роли `operator` любой набор с
     * правами справочника (клетка `officeEquipment × counterparty` — `forbidden`), но НЕ отбивает
     * собранный руками набор из одних `serviceRequests.*`: клетка `service × counterparty` —
     * `scoped`, и такой набор подрядчику выдать можно. Права хода у него уже есть от типа
     * контрагента, и `assign` сверху сделал бы исполнителя распорядителем очереди: он снимал бы с
     * заявки свою компанию и ставил чужую — или наоборот, забирал бы себе чужие заявки.
     *
     * ЖИВЫХ ДЕРЖАТЕЛЕЙ ПАРЫ НЕТ, и это проверено перебором, а не рассуждением
     * (`grants-contracts.test.ts`): у сервисной компании есть `estimate` и нет `assign`, у
     * «Ведения» и ИТ-службы — наоборот, у набора исполнителя нет ни того ни другого. Поэтому
     * строка ничего не отбирает сегодня и стоит здесь ради завтрашней свободной сборки.
     *
     * Пара `estimate` + `status` в таблицу НЕ добавляется: это собственный набор подрядчика
     * (`COUNTERPARTY_TYPE_PERMISSIONS.service`), и запрет отобрал бы у него сегодняшний цикл.
     */
    permissions: ['serviceRequests.estimate', 'serviceRequests.assign'],
    reason: 'Исполнитель сам себе распределяет работу',
  },
];

/**
 * Первая нарушенная пара в наборе прав или `null`. Первая, а не все: сообщение об отказе должно
 * называть виновников, и одной пары для этого достаточно — как `incompatibleAddon` называет одну
 * несовместимую надстройку.
 *
 * Принимает права, а не субъект, намеренно. Исключение для администратора — вопрос роли, и решает
 * его вызывающий; получи эта функция субъект, соблазн вывести исключение из объёма его прав
 * появился бы прямо здесь, а такое правило отключает себя в той самой операции, которую и должно
 * ловить.
 */
export function conflictingPermissions(permissions: Iterable<Permission>): GrantConflict | null {
  const held = new Set(permissions);
  return (
    GRANT_CONFLICTS.find(
      ({ permissions: [first, second] }) => held.has(first) && held.has(second),
    ) ?? null
  );
}

/**
 * Код нарушения выдачи — то, чем маршрут отвечает, а экран подсвечивает. Строка, а не номер:
 * номера читаются только через таблицу, а `permission_not_grantable` в журнале объясняет себя сам.
 *
 * Пять кодов — пять барьеров чек-листа готовности (план §15), и шестого здесь нет намеренно:
 * `expectedImpactHash`, блокировки «набор → учётки» и запись в журнал — свойства операции, а не
 * данных, и живут они там, где есть транзакция.
 */
export type GrantViolationCode =
  /** Право из `NON_GRANTABLE_PERMISSIONS` (инвариант 5 плана §8). */
  | 'permission_not_grantable'
  /** Роль, которой полномочия не выдаются вовсе, — сегодня это `driver` (инвариант 6). */
  | 'role_not_grantable'
  /** Модуль права помечен `forbidden` для оси этой роли (инвариант 4, матрица `grant-scope.ts`). */
  | 'module_forbidden_for_axis'
  /** У итоговых прав субъекта нет требуемого по `PERMISSION_REQUIRES` (ADR 0021, §13.1). */
  | 'requirement_missing'
  /** Итоговые права субъекта сводят пару из `GRANT_CONFLICTS` (разделение обязанностей). */
  | 'duty_conflict';

/**
 * Нарушение выдачи: код для машины, текст для человека и виновники по отдельным полям — чтобы
 * экрану не приходилось разбирать сообщение обратно.
 *
 * Модуль в полях не повторяется: он однозначно выводится из права через `PERMISSION_CATALOG`, и
 * второе его написание разошлось бы с первым — как только кто-нибудь перенесёт право в другой
 * модуль витрины.
 */
export interface GrantViolation {
  readonly code: GrantViolationCode;
  /** Текст отказа по-русски, называющий виновника: право, роль, набор — а не «недопустимая комбинация». */
  readonly message: string;
  /** Право-виновник: у барьера требований — то, что осталось без поддержки. */
  readonly permission?: Permission;
  /** Чего не хватает итоговым правам субъекта (только `requirement_missing`). */
  readonly requires?: Permission;
  /** Роль-виновник (`role_not_grantable`, `module_forbidden_for_axis`). */
  readonly role?: Role;
  /** Пара разделения обязанностей вместе с её причиной (только `duty_conflict`). */
  readonly conflict?: GrantConflict;
}

/**
 * Вход единой проверки выдачи.
 *
 * `subjectPermissionsAfter` и `subjectRole` объявлены **обязательными и нулевыми**, а не
 * необязательными, и это не педантизм: два барьера из пяти проверяются только по итогу, и
 * вызывающий, забывший прислать итог, получил бы «нарушений нет» на операции, которая как раз
 * ломает инвариант. Обязательное поле заставляет компилятор спросить, и `null` в нём — осознанный
 * ответ «операции над учёткой в этом вызове нет» (так конструктор сохраняет состав набора, ещё
 * никому не выданного).
 */
export interface GrantAssignmentCheck {
  /**
   * Роли, которым набор разрешён (`grant_roles`), — либо роль держателя, если проверяется выдача.
   * Пустой список законен: набор, который ещё никому нельзя выдать, ничего не открывает.
   */
  readonly roles: readonly Role[];
  /** Состав набора (`grant_permissions`). Пустой список законен — он просто ничего не даёт. */
  readonly permissions: readonly Permission[];
  /**
   * **Итоговые** эффективные права субъекта после операции — то, что вернёт `can` по всем
   * источникам (роль, тип контрагента, все наборы), а не состав проверяемого набора. `null` —
   * субъекта в операции нет.
   */
  readonly subjectPermissionsAfter: readonly Permission[] | null;
  /** Роль субъекта операции; нужна ровно для оговорки об администраторе. `null` — субъекта нет. */
  readonly subjectRole: Role | null;
  /** Название набора для текста отказа: администратор читает имя, а не код. */
  readonly grantLabel?: string;
}

/** Право в тексте отказа — подписью и кодом: подпись читают, код ищут в журнале и в форме. */
function permissionTitle(permission: Permission): string {
  return `«${PERMISSION_CATALOG[permission].label}» (${permission})`;
}

/**
 * **Единая проверка выдачи — все барьеры в одном месте.** Список нарушений, а не исключение: одно
 * и то же спрашивают маршрут (чтобы отказать), конструктор (чтобы не дать нажать) и тесты, и
 * бросающая функция заставила бы экран узнавать про второе нарушение после исправления первого.
 * Пустой список означает «выдача законна».
 *
 * Барьеры делятся на два вида, и путать их нельзя.
 *
 * **Барьеры набора** (1–3) считаются по присланным `roles` и `permissions`: невыдаваемое право,
 * роль, не принимающая наборов, и декартово произведение против матрицы осей. Они проверяются до
 * записи, потому что после неё проверять нечего — данные уже показаны (план §8, инвариант 4).
 *
 * **Барьеры итога** (4–5) считаются по `subjectPermissionsAfter`: требования прав друг к другу и
 * разделение обязанностей. Внутри набора их проверять нельзя, и системный «Оператор (оргтехника)» —
 * прямое доказательство: `officeEquipment.write` даёт он, а `officeEquipment.read` приходит от
 * базовой роли. Набор сам по себе инвариант нарушает, субъект — нет (§13.1).
 *
 * **Оговорка об администраторе привязана к роли, а не к объёму прав.** У роли `admin` весь словарь,
 * то есть обе конфликтующие пары есть у неё всегда; без оговорки любая операция над её учёткой
 * (смена области, выключение, правка) отклонялась бы отказом, который никто не может устранить.
 * Формулировка «не проверяем того, у кого есть все права модуля конфликта» отвергнута планом
 * (§13.1) и здесь не реализована: исполнитель-подрядчик `service` уже имеет четыре права модуля
 * обслуживания из десяти, ни одно из недостающих не защищено, — и правило выключало бы проверку
 * ровно в той операции, которая дособирает модуль и создаёт запрещённую пару. Правило, отменяющее
 * себя ростом доступа, — не правило.
 *
 * **Одна причина — одно нарушение.** Невыдаваемое право до матрицы не доходит (иначе `users.manage`
 * ловился бы дважды — запретом и клеткой модуля `admin`), а роль `driver` не проверяется по
 * матрице вовсе: ей отказано целиком, и перечислять вдогонку её запрещённые клетки значило бы
 * выдать администратору список, из которого не видно главного.
 *
 * Порядок нарушений устойчив и объявлен: сначала барьеры набора в порядке барьеров (право → роль →
 * пары «роль × право», роли снаружи), затем барьеры итога, причём требования — в порядке словаря
 * `PERMISSIONS`, а не в том, в каком права субъекта пришли. Иначе один и тот же отказ читался бы
 * по-разному от вызова к вызову.
 */
export function validateGrantAssignment(check: GrantAssignmentCheck): readonly GrantViolation[] {
  const violations: GrantViolation[] = [];
  const where = check.grantLabel ? `Набор «${check.grantLabel}»: ` : '';

  // Барьер 1: право не из `NON_GRANTABLE_PERMISSIONS`. Дубликаты в составе — не два нарушения:
  // право, пришедшее в форме дважды, виновато один раз.
  const grantable: Permission[] = [];
  for (const permission of new Set(check.permissions)) {
    if (isGrantable(permission)) {
      grantable.push(permission);
      continue;
    }
    violations.push({
      code: 'permission_not_grantable',
      permission,
      message: `${where}право ${permissionTitle(permission)} не выдаётся полномочиями ни при каких условиях — ни системным набором, ни собранным администратором.`,
    });
  }

  // Барьер 2: роль `driver` наборов не принимает (план §8, инвариант 6).
  const roles: Role[] = [];
  for (const role of new Set(check.roles)) {
    if (role !== 'driver') {
      roles.push(role);
      continue;
    }
    violations.push({
      code: 'role_not_grantable',
      role,
      message: `${where}роль «${roleLabels.driver}» полномочий не принимает ни одним способом: кабинет открывает задание конкретного работника, и добавить к нему чужие права нельзя.`,
    });
  }

  // Барьер 3: декартово произведение «роль × право» против матрицы осей.
  for (const role of roles) {
    for (const permission of grantable) {
      const { module } = PERMISSION_CATALOG[permission];
      if (grantScopeRule(role, module) !== 'forbidden') continue;
      violations.push({
        code: 'module_forbidden_for_axis',
        permission,
        role,
        message: `${where}право ${permissionTitle(permission)} нельзя выдать роли «${roleLabels[role]}»: у модуля «${permissionModuleLabels[module]}» нет фильтрации по её области (${roleScopeAxisLabels[roleScopeAxis(role)]}), и набор открыл бы данные всей компании.`,
      });
    }
  }

  const after = check.subjectPermissionsAfter;
  if (after === null) return violations;
  const held = new Set<Permission>(after);

  // Барьер 4: «модуль закрывается чтением» — по итоговым правам субъекта (ADR 0021, §13.1).
  for (const permission of PERMISSIONS) {
    if (!held.has(permission)) continue;
    for (const required of PERMISSION_REQUIRES[permission] ?? []) {
      if (held.has(required)) continue;
      violations.push({
        code: 'requirement_missing',
        permission,
        requires: required,
        message: `${where}у учётки есть право ${permissionTitle(permission)}, а ${permissionTitle(required)} — нет: действие открыто в модуле «${permissionModuleLabels[PERMISSION_CATALOG[permission].module]}», которого учётка не видит.`,
      });
    }
  }

  // Барьер 5: разделение обязанностей — по итогу и с оговоркой об администраторе.
  if (check.subjectRole === 'admin') return violations;
  for (const conflict of GRANT_CONFLICTS) {
    const [first, second] = conflict.permissions;
    if (!held.has(first) || !held.has(second)) continue;
    violations.push({
      code: 'duty_conflict',
      conflict,
      message: `${where}права ${permissionTitle(first)} и ${permissionTitle(second)} сходятся в одной учётке: ${conflict.reason}. Итог считается по всем источникам — половину пары может давать роль или другой набор.`,
    });
  }

  return violations;
}

// ── Каталог наборов: запросы, тела и витрина (план §12, этап 3) ──
//
// Выше в файле — решения и барьеры, то есть данные. Ниже — контракт серверного каталога: чем
// администратор спрашивает список, чем создаёт и правит набор и что видит в ответе. Живут они в
// одном файле по той же причине, по которой в одном файле живут словарь прав и его схема: тело
// запроса обязано ссылаться на те же константы, по которым считаются барьеры, — иначе форма покажет
// право, которого проверка не знает.

/**
 * Поля сортировки каталога. Числа держателей здесь нет намеренно: оно считается отдельным
 * агрегатом по `user_grants`, и сортировка по нему потребовала бы соединения в основном запросе
 * ради колонки, которую читают глазами. Появится — придёт вместе с окном реестра выдач.
 */
export const GRANT_SORT_FIELDS = ['code', 'name', 'isSystem', 'createdAt'] as const;

/**
 * Отбор по классу набора (ADR 0106, решение 2). Три значения, а не булев флаг: «все» — обычное
 * состояние экрана, и `system=false` означало бы «только пользовательские», то есть отсутствие
 * фильтра стало бы неотличимо от выбранного «нет».
 */
export const GRANT_KINDS = ['all', 'system', 'custom'] as const;
export type GrantKindFilter = (typeof GRANT_KINDS)[number];

export const grantListQuerySchema = baseListQuery(GRANT_SORT_FIELDS).extend({
  kind: z.enum(GRANT_KINDS).default('all'),
  /**
   * Отбор совместимостью с ролью (`grant_roles`): окно учётки показывает чекбоксами только те
   * наборы, которые выбранной в форме роли выдать можно (план «полномочия назначаются в окне
   * учётки», Р2). Без него форме пришлось бы читать каталог целиком и отсеивать несовместимые
   * самой — то есть повторять правило совместимости вторым его написанием, а расходятся такие
   * пары на первой же правке `grant_roles`.
   *
   * Необязателен: каталог администратора роль не спрашивает — он ведёт наборы, а не выдаёт их, и
   * отбор там означал бы, что часть наборов из своего же реестра не видно.
   *
   * Роль `driver` схемой не отсеивается намеренно, как и в `grantRoleListSchema`: наборов она не
   * принимает вовсе (барьер 2), и ответ «список пуст» здесь честнее отказа 400 — спрашивают
   * каталог, а не выдачу.
   */
  role: roleSchema.optional(),
});
export type GrantListQuery = z.infer<typeof grantListQuerySchema>;

/**
 * Код пользовательского набора. Латиница нижнего регистра, цифры и подчёркивание — тот же вид, что
 * у системных кодов, и не из вкуса: код попадает в журнал, в реестр выдач и в ключи типизированных
 * таблиц кода (`GRANT_MODULE_WIDE_SCOPE`), а пробелы и регистр там читаются как две разные записи.
 *
 * Системный код администратору не отдаётся. Уникальный индекс в базе его и так не пустит, пока
 * системная строка жива, — но отказ пришёл бы «код занят», и это была бы неправда о причине:
 * запрет тут не в занятости, а в том, что таблица сквозной области ищет набор **по коду**, и
 * пользовательская строка под кодом визы ИТ получила бы область двух модулей оргтехники, которую
 * решение 2 запрещает пользовательским наборам вовсе.
 *
 * Список — `ALL_SYSTEM_GRANT_CODES`, то есть вместе с административными (§10, этап 4). Область они
 * не расширяют, но код у них тот же ключ: под ним их ищет миграция каталога, и пользовательская
 * копия «Архивариуса» под кодом системного оставила бы будущую правку каталога без строки, которую
 * она правит.
 */
export const grantCodeSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[a-z][a-z0-9_]*$/u, 'Код: латиница в нижнем регистре, цифры и подчёркивание')
  .refine((code) => !(ALL_SYSTEM_GRANT_CODES as readonly string[]).includes(code), {
    message: 'Такой код занят системным полномочием — выберите другой',
  });

export const grantNameSchema = z.string().trim().min(1).max(255);
/** Описание — то, чем набор объясняет себя в каталоге; пустое законно, но короче названия не бывает. */
export const grantDescriptionSchema = z.string().trim().max(2000);

/**
 * Состав набора. Схема принимает **любое** право словаря, включая невыдаваемые, и это осознанно:
 * барьеры считает `validateGrantAssignment`, и отказ обязан называть виновника словами («право
 * „Ведение учётных записей“ не выдаётся полномочиями ни при каких условиях»), а не приходить общим
 * «недопустимое значение перечисления». Схема здесь отвечает только за то, что право существует.
 */
export const grantPermissionListSchema = z.array(z.enum(PERMISSIONS)).max(PERMISSIONS.length);
/** Совместимые роли — по той же причине без отсева `driver`: барьер называет её сам. */
export const grantRoleListSchema = z.array(roleSchema).max(ROLES.length);

// ── Отпечаток последствий (`expectedImpactHash`, ADR 0106, решение 7; план §13.1) ──

/**
 * Операции над учёткой, которые администратор подтверждает по предпросмотру. Две, и обе меняют
 * состав назначений: выдача создаёт строку `user_grants`, отзыв её удаляет.
 */
export const GRANT_ASSIGNMENT_OPERATIONS = ['assign', 'revoke'] as const;
export type GrantAssignmentOperation = (typeof GRANT_ASSIGNMENT_OPERATIONS)[number];

/**
 * Все операции, меняющие доступ через полномочия, — то есть все, у которых бывает предпросмотр.
 * Правка набора стоит рядом с выдачей и отзывом намеренно: отпечаток у них общий, потому что
 * пересекаются они на одних и тех же строках (§13.1, правило блокировок), и два разных отпечатка
 * означали бы, что одна операция не видит подтверждения другой.
 */
export const GRANT_IMPACT_OPERATIONS = ['update', ...GRANT_ASSIGNMENT_OPERATIONS] as const;
export type GrantImpactOperation = (typeof GRANT_IMPACT_OPERATIONS)[number];

/** Затронутая учётка вместе с версией её токенов — слагаемое отпечатка. */
export interface GrantImpactAccount {
  readonly userId: string;
  /** `users.auth_version`: готовый счётчик изменений доступа — он растёт при каждой такой правке. */
  readonly authVersion: number;
}

/**
 * Всё, от чего зависел показанный предпросмотр (§13.1, таблица слагаемых). Пять полей, и ни одно не
 * украшение:
 *
 * | Слагаемое                                | Что ловит                                     |
 * | ---------------------------------------- | --------------------------------------------- |
 * | `operation` + `grantId` / `userId`       | подмену действия при том же состоянии базы     |
 * | `permissions` + `roles` (пост-состав)    | предпросмотр состава A и сохранение состава B  |
 * | `version`                                | правку состава кем-то другим                   |
 * | `assignmentIds`                          | появление и исчезновение держателей            |
 * | `accounts`                               | смену роли или другого набора у держателя       |
 *
 * **Первые две строки — то, без чего отпечаток не работает вовсе.** Отпечаток только из состояния
 * базы не связан с тем, что подтверждали: администратор смотрит предпросмотр состава A, отправляет
 * состав B, база за это время не менялась — хеш совпадает, и сервер применяет то, чего никто не
 * видел.
 *
 * **Заменить пост-состав на `effectiveDelta` нельзя** (§13.1). Delta считает не то, что подтверждают,
 * а то, что из этого следует, и следствие теряет различия: составы `{read, update}` и `{update}`
 * дадут одинаковую delta, если `read` держателю и так приходит от роли, — а это два разных набора, и
 * второй перестанет работать в день, когда роль сменят. Правка `grant_roles` не меняет delta нынешних
 * держателей вовсе: она про то, кому набор можно выдать завтра. Delta допустима только **вдобавок**
 * к каноническому пост-составу, и никогда вместо него.
 */
export interface GrantImpactInput {
  readonly operation: GrantImpactOperation;
  readonly grantId: string;
  /** Цель у выдачи и отзыва; `null` у правки набора — там цель сам набор, а не одна учётка. */
  readonly userId: string | null;
  /** Предлагаемый пост-состав: права. У выдачи и отзыва — нынешний состав, он не меняется. */
  readonly permissions: readonly Permission[];
  /** Предлагаемый пост-состав: совместимые роли. */
  readonly roles: readonly Role[];
  readonly version: number;
  /**
   * Идентификаторы назначений, от которых зависел расчёт: у правки набора — назначения всех его
   * держателей, у выдачи и отзыва — **все** назначения целевой учётки.
   *
   * Все, а не одно правимое: итог считается по сумме наборов учётки (разделение обязанностей
   * возникает суммой, §13.1), и назначение, появившееся у неё между предпросмотром и сохранением,
   * меняет и дельту, и приговор барьеров.
   */
  readonly assignmentIds: readonly string[];
  /** Затронутые учётки: у правки — держатели, у выдачи и отзыва — одна целевая. */
  readonly accounts: readonly GrantImpactAccount[];
}

/**
 * Каноническая строка отпечатка. Именно строка, а не объект: порядок полей формы и порядок галочек
 * в ней на отпечаток влиять не должны, поэтому все списки сортируются, а поля печатаются в
 * объявленном здесь порядке. Требование одно и проверяется тестом: **разный запрос — разный хеш**.
 *
 * Сам хеш считает сервер (`grantImpactHash` в `services/grant-catalog.ts`): у контрактов нет
 * криптографии, которая работала бы одинаково в браузере и в Node, а порталу считать его и не нужно —
 * он получает отпечаток предпросмотром и возвращает его как есть. Каноническая же строка живёт здесь,
 * рядом с барьерами: она описывает, **что** покрыто подтверждением, а это решение модели.
 *
 * Версия в первой строке (`grant-impact/v1`) нужна выкату: правка состава слагаемых обязана обесценить
 * отпечатки, выданные прежней версией, — иначе подтверждение, посчитанное по четырём слагаемым,
 * сошлось бы с проверкой по пяти.
 */
export function grantImpactCanonical(input: GrantImpactInput): string {
  const list = (values: readonly string[]): string => [...new Set(values)].sort().join(',');
  return [
    'grant-impact/v1',
    `op=${input.operation}`,
    `grant=${input.grantId}`,
    `user=${input.userId ?? '-'}`,
    `version=${input.version}`,
    `permissions=${list(input.permissions)}`,
    `roles=${list(input.roles)}`,
    `assignments=${list(input.assignmentIds)}`,
    `accounts=${list(input.accounts.map((a) => `${a.userId}:${a.authVersion}`))}`,
  ].join('\n');
}

/**
 * Отпечаток в теле запроса: шестнадцатеричный sha256, каким его вернул предпросмотр.
 *
 * Формат проверяется схемой, хотя портал отпечаток только передаёт обратно: мусор в поле обязан
 * отвечать 400 («так отпечатки не выглядят»), а не 409 («данные устарели, перечитайте») — второй
 * ответ отправил бы портал в бесконечный цикл перечитывания того, что не изменится.
 */
export const expectedImpactHashSchema = z
  .string()
  .trim()
  .length(64)
  .regex(/^[0-9a-f]+$/u, 'Отпечаток последствий: 64 шестнадцатеричных знака');

/**
 * Создание пользовательского набора. Признака `isSystem` в теле нет и быть не может: системный
 * набор заводит миграция, потому что только он несёт объявленный в коде scope-эффект (решение 2), а
 * поле в схеме означало бы, что признак — вопрос запроса.
 */
export const createGrantSchema = z.object({
  code: grantCodeSchema,
  name: grantNameSchema,
  description: grantDescriptionSchema.default(''),
  permissions: grantPermissionListSchema.default([]),
  roles: grantRoleListSchema.default([]),
});
export type CreateGrantInput = z.infer<typeof createGrantSchema>;

/**
 * Правка набора. Кода здесь нет намеренно — переименование кода не переименовывает набор, а
 * отвязывает его от таблиц кода (см. `SYSTEM_GRANT_CODES`), и для пользовательского набора это
 * та же потеря связи с журналом и реестром выдач.
 *
 * `expectedVersion` обязателен, а не необязателен «для совместимости»: правка состава меняет доступ
 * всем держателям сразу, и запрос без версии — это согласие применить её к состоянию, которого
 * отправитель не видел.
 *
 * **`expectedImpactHash` обязателен рядом с версией, и одно другое не заменяет.** Версии мало по
 * решению 7: состав не менялся, версия та же, а набор тем временем выдали пятнадцатому человеку — или
 * одному из держателей сменили роль либо другой набор. Версия совпадёт, а применится не то, что
 * подтверждали. Отпечаток покрывает и это, и сам присланный состав (см. `GrantImpactInput`).
 *
 * Обратное тоже верно, поэтому версия не убрана: отпечаток отвечает только «показанное устарело», а
 * версия называет причину — «состав правил другой администратор (версия 5, а не 4)». Отказ без этой
 * половины отправлял бы администратора искать, что же изменилось, по всей карточке.
 */
export const updateGrantSchema = z.object({
  expectedVersion: z.number().int().min(1),
  /** Отпечаток последствий, каким его вернул `POST /grants/:id/preview` для **этого** состава. */
  expectedImpactHash: expectedImpactHashSchema,
  name: grantNameSchema.optional(),
  description: grantDescriptionSchema.optional(),
  /** Полный состав; отсутствие поля — «не трогать права», как у наборов в карточке отдела. */
  permissions: grantPermissionListSchema.optional(),
  /** Полный список ролей; отсутствие поля — «не трогать роли». */
  roles: grantRoleListSchema.optional(),
});

/**
 * Тело предпросмотра правки набора — те же поля состава, что у `PATCH`, но без версии и отпечатка:
 * их предпросмотр как раз и выдаёт.
 *
 * Отсутствие поля читается так же, как в правке («не трогать»), и это обязательное свойство, а не
 * совпадение: пост-состав входит в отпечаток, поэтому предпросмотр и сохранение должны достраивать
 * пропущенные поля из строки набора **одинаково**. Разойдись они — отпечаток не совпал бы никогда, и
 * правка стала бы невыполнимой.
 */
export const grantUpdatePreviewSchema = z.object({
  name: grantNameSchema.optional(),
  permissions: grantPermissionListSchema.optional(),
  roles: grantRoleListSchema.optional(),
});
export type GrantUpdatePreviewInput = z.infer<typeof grantUpdatePreviewSchema>;

/**
 * Тело предпросмотра выдачи и отзыва: что собираются сделать и с каким набором. Учётка приходит
 * адресом — предпросмотр висит на её карточке, а не на каталоге.
 *
 * Вид операции в теле, а не два маршрута: ответ у них одинаковый по форме (дельта плюс отпечаток), а
 * вид операции входит в отпечаток слагаемым — «подмену действия при том же состоянии базы» ловит
 * именно он. Два маршрута с одинаковым телом означали бы, что отпечаток выдачи примет отзыв.
 */
export const grantAssignmentPreviewSchema = z.object({
  operation: z.enum(GRANT_ASSIGNMENT_OPERATIONS),
  grantId: z.string().uuid(),
});
export type GrantAssignmentPreviewInput = z.infer<typeof grantAssignmentPreviewSchema>;

/** Тело выдачи набора учётке: какой набор и под какой отпечаток. */
export const assignGrantSchema = z.object({
  grantId: z.string().uuid(),
  expectedImpactHash: expectedImpactHashSchema,
});
export type AssignGrantInput = z.infer<typeof assignGrantSchema>;

/**
 * Отзыв: набор назван в адресе, в строке запроса — только отпечаток. Тем же приёмом удаляются акт ТО
 * и точка маршрута (версия строкой запроса): тело у `DELETE` доходит не через каждый прокси, а
 * подтверждение операции обязано доходить всегда.
 */
export const revokeGrantQuerySchema = z.object({
  expectedImpactHash: expectedImpactHashSchema,
});
export type RevokeGrantQuery = z.infer<typeof revokeGrantQuerySchema>;
export type UpdateGrantInput = z.infer<typeof updateGrantSchema>;

// ── Полномочия в теле учётки (план «полномочия назначаются в окне учётки», §4) ──
//
// Окно учётки задаёт полномочия одной операцией — вместе с ролью, областью и активацией, — поэтому
// тело правки несёт не список того, что должно остаться, а **высказывание о каждом наборе, о
// котором операция что-то решает** (Р3): у снимаемого набора в списке оставшихся нет строки по
// построению, и «пусто» одинаково означало бы «снял» и «не видел».
//
// Схемы живут здесь, а не в `users.ts`, по той же причине, по которой здесь же лежат барьеры: тело
// учётки только ссылается на границы полномочий, и вторая их копия рядом с формой разошлась бы с
// первой на первой же правке каталога.

/**
 * Сколько назначений остаётся у учётки после операции — граница **итога**.
 *
 * **Это граница запроса, а не бизнес-правило.** Она стоит от опечатки в клиенте и от тела, которое
 * никто не собирался присылать; утверждения «столько полномочий одному человеку не выдают» в ней
 * нет — сколько наборов осмысленно у одной должности, решает администратор в каталоге.
 *
 * Значение выбрано с запасом над фактом, а не круглым числом из головы: поставочный каталог знает
 * девять системных наборов (два перенесённых из надстроек, четыре административных, три ролевых),
 * а живой держатель носит один-два. Тридцать два — запас втрое над всем каталогом целиком, то есть
 * предел, до которого нельзя дойти, не собрав в конструкторе десятки новых наборов и не выдав их
 * одному человеку.
 *
 * **Ниже факта её ставить нельзя, и это единственное жёсткое требование к значению.** Константа,
 * оказавшаяся меньше числа уже выданных назначений, запрещает сохранить карточку человека, которому
 * ничего не добавляли: правка телефона упёрлась бы в предел полномочий (§4.2). Поэтому перед
 * выкатом идёт preflight по проду (§7.1) — сверяются с данными, а не с этим комментарием.
 *
 * **Предел общий у обоих путей выдачи и проверяется сервером, а не схемой.** Схема тела видит
 * высказывание, а не итог: сколько назначений останется, известно только после соединения тела с
 * уже выданными, а их знает база. Тот же предел обязана считать точечная выдача
 * (`POST /users/:id/grants`), иначе она набьёт учётке больше назначений, чем форма способна
 * высказать, — и правило полноты (§4.2) станет невыполнимым, то есть карточка такого человека
 * перестанет сохраняться вовсе.
 */
export const MAX_ASSIGNED_GRANTS = 32;

/**
 * Сколько строк бывает в высказывании — граница **самого тела**, и она вдвое больше границы итога.
 *
 * Полная замена сводит в одном теле обе стороны разницы: строки снимаемых наборов (`selected:
 * false`) и строки выдаваемых. Одной границы на оба смысла не хватило бы ровно в этом случае —
 * запрос, снимающий `MAX_ASSIGNED_GRANTS` назначений и выдающий столько же, законен по итогу, а по
 * длине отклонялся бы. Отсюда две константы вместо одной: `MAX_ASSIGNED_GRANTS` считает итог,
 * `MAX_GRANT_STATEMENTS` — высказывание (§4.2).
 *
 * Выражена умножением, а не вторым числом: связь границ — само решение, и разъехавшись, они
 * запретили бы полную замену молча.
 */
export const MAX_GRANT_STATEMENTS = 2 * MAX_ASSIGNED_GRANTS;

/**
 * Строка высказывания: про какой набор говорят, какой его состав при этом показывали и стоит ли
 * галочка.
 *
 * `version` — не «оптимистическая блокировка ради блокировки»: состав набора могли изменить между
 * открытием формы и сохранением, и человек получил бы права, которых в подсказке не видел. Сверка
 * идёт под блокировками и только по участвующим наборам (Р7), а отпечатка последствий здесь нет
 * вовсе: вход операции — итог, названный явно, а не чужое состояние, которое подтверждают.
 *
 * `selected` читается **относительно диапазона роли**, и смыслов у `false` два (§4.3): у
 * управляемого назначения это команда снять, у назначения вне диапазона итоговой роли —
 * подтверждение того, что оно перестаёт действовать, и строка остаётся жить. Различить их схема не
 * может: диапазон считается по итоговой роли и по `grant_roles`, то есть по базе.
 */
export const grantStatementSchema = z.object({
  /** Набор (`grants.id`), а не код: код у пользовательского набора правится, идентификатор — нет. */
  id: uuidSchema,
  /** Версия состава, показанная формой (`GrantDto.version`); нумерация с единицы. */
  version: z.number().int().min(1),
  /** Галочка в форме — итог, которого добивается администратор, а не совершаемое действие. */
  selected: z.boolean(),
});
export type GrantStatement = z.infer<typeof grantStatementSchema>;

/**
 * Высказывание целиком: длина в границах тела и **ровно одна строка на набор**.
 *
 * Уникальность `id` проверяется здесь, а не разрешается умолчанием на сервере, и выбор этот не
 * между строгостью и снисходительностью (§4.3). `{selected: true}` и `{selected: false}` про один
 * набор противоречат друг другу, а любое правило разрешения — «побеждает последнее», «побеждает
 * снятие» — означает, что за администратора решил порядок сериализации формы. Дубликат с одинаковым
 * `selected`, но разными `version` — та же ошибка с другой стороны: непонятно, какой состав
 * показывали, а именно его подписывают. Поэтому дубль отклоняется в обоих видах.
 *
 * **Чего эта схема проверить не может — и не пытается.** Всё, что требует базы, остаётся серверу, и
 * искать проверку здесь не нужно:
 *
 * - `grants` при `role = null` — полномочия выдаются поверх должности (§4.1);
 * - полнота высказывания: каждое управляемое и переключаемое сменой роли назначение обязано быть
 *   названо (§4.2) — что выдано учётке, знает только база;
 * - границы молчания: запрос без поля не проводит смену роли, переключающую действие назначений;
 * - `MAX_ASSIGNED_GRANTS` — граница итога, а не длины тела (см. выше);
 * - совместимость набора с итоговой ролью, отказ на снятие `origin = 'migration'`, сверка версий и
 *   пять барьеров выдачи.
 */
export const grantStatementListSchema = z
  .array(grantStatementSchema)
  .max(
    MAX_GRANT_STATEMENTS,
    `Полномочий в одном запросе не больше ${MAX_GRANT_STATEMENTS} — откройте карточку заново`,
  )
  .superRefine((rows, ctx) => {
    const seen = new Set<string>();
    rows.forEach((row, index) => {
      if (!seen.has(row.id)) {
        seen.add(row.id);
        return;
      }
      ctx.addIssue({
        code: 'custom',
        message: 'Об одном полномочии сказано дважды — оставьте одну строку',
        path: [index, 'id'],
      });
    });
  });
export type GrantStatementList = z.infer<typeof grantStatementListSchema>;

/**
 * Происхождение назначения (`user_grants.origin`): выдал человек, создал перевод ролей или сохранил
 * доступ выкат, переводивший модуль в назначаемый.
 *
 * **Третье значение — не косметика** (план `docs/mechanization-approval-and-grants-plan.md`, Р9).
 * Когда право уходит из состава роли, доступ живым учёткам сохраняется посевом до перезапуска
 * нового кода, и назвать этот посев одним из двух прежних значений нельзя:
 *
 * - `manual` соврал бы «выдал администратор»: автора у посева нет вовсе, и реестр выдач показывал
 *   бы человека там, где его не было;
 * - `migration` означает **взведённое** назначение перевода ролей, и форма учётки намеренно
 *   запирает такие выдачи (`lockedGrantIds`): снять их галочкой нельзя, иначе перевод потеряет
 *   права, на которых стоит. Посев же обязан сниматься как обычная выдача — ради возможности
 *   сузить круг администратор его и получает.
 *
 * `backfill` читается как «доступ сохранён при переводе модуля в назначаемый»: показывается своей
 * подписью, снимается и возвращается галочкой, ссылки на перевод не имеет никогда (CHECK
 * `user_grants_migration_origin_check` остаётся прежним — он про `migration`).
 */
export const GRANT_ORIGINS = ['manual', 'migration', 'backfill'] as const;
export type GrantOrigin = (typeof GRANT_ORIGINS)[number];

/** Подпись происхождения для реестра выдач и карточки учётки: администратор читает слова. */
export const grantOriginLabels: Record<GrantOrigin, string> = {
  manual: 'выдано администратором',
  migration: 'ждёт перевода роли',
  backfill: 'сохранено при переводе модуля',
};

/**
 * Набор в каталоге. Права разложены на два поля, и это прямое следствие решения 3 ADR 0106:
 * право хранится в базе текстом, справочника прав в базе нет, и выкат, снявший право из словаря,
 * оставляет в наборе строку-сироту. Доступа она не даёт никогда — вычислитель её игнорирует, — но и
 * молчать о ней нельзя: в системном наборе такая строка означает забытую миграцию каталога, а в
 * пользовательском — право, которое администратор когда-то отметил, а портал больше не знает.
 */
export interface GrantDto {
  id: string;
  code: string;
  name: string;
  description: string;
  /** Системный набор: правке и удалению не подлежит, только он несёт scope-эффект (решение 2). */
  isSystem: boolean;
  /** Версия состава — её же присылает обратно правка в `expectedVersion`. */
  version: number;
  /** Состав набора в порядке словаря `PERMISSIONS`: список читают глазами. */
  permissions: Permission[];
  /** Строки состава, которых нет в словаре прав, — «право больше не существует». */
  unknownPermissions: string[];
  /** Роли, которым набор разрешено назначать, в порядке словаря `ROLES`. */
  roles: Role[];
  /** Сколько живых назначений у набора — то, чем удаление отвечает 409. */
  holderCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Держатель набора — строка реестра выдач (§12): кому выдано, кем, когда и на каком основании.
 *
 * `roleMismatch` — то самое «роль больше не в списке» из §13.1: назначение осталось живым, потому
 * что молчаливый массовый отзыв опаснее несоответствия, но новых выдач этой роли уже не будет.
 * Пометка обязана быть в ответе, а не выводиться экраном из пересечения ролей: пересечение экран
 * посчитает по составу набора, а несоответствие — свойство назначения.
 */
export interface GrantHolderDto {
  /** Идентификатор назначения (`user_grants.id`) — им следующая волна отзывает выдачу. */
  assignmentId: string;
  userId: string;
  fullName: string;
  email: string;
  role: Role | null;
  /** Роль держателя не входит в `roles` набора: выдача жива, прав по ней набор не даёт. */
  roleMismatch: boolean;
  /** Доступ в портал у держателя открыт. Выключенная учётка держит набор наравне с действующей. */
  isActive: boolean;
  /** Учётка в архиве (ADR 0070). Назначение остаётся и мешает удалить набор — это не ошибка. */
  isArchived: boolean;
  grantedAt: string;
  /** Кто выдал; `null` — учётку автора удалили насовсем (`granted_by` под `SET NULL`). */
  grantedByName: string | null;
  origin: GrantOrigin;
}

/** Карточка набора: каталожная строка плюс реестр его выдач. */
export interface GrantCardDto extends GrantDto {
  holders: GrantHolderDto[];
}

/** Нарушения по итогу у одного держателя: отказ обязан называть, кем именно операция запрещена. */
export interface GrantHolderViolationsDto {
  userId: string;
  fullName: string;
  violations: GrantViolation[];
}

/**
 * Тело отказа выдачи — то, что портал раскладывает по форме, не разбирая текст обратно.
 *
 * Две половины, потому что барьеров два вида (см. `validateGrantAssignment`): `violations` — про
 * сам набор (невыдаваемое право, роль `driver`, запрещённая клетка матрицы осей), `holders` — про
 * итог у держателей (требования прав и разделение обязанностей). Экран показывает их по-разному:
 * первые подсвечивают галочку в конструкторе, вторые — строку реестра выдач.
 */
export interface GrantValidationDetailsDto {
  violations: GrantViolation[];
  holders: GrantHolderViolationsDto[];
}

/** Тело отказа удаления: набор выдан, и вот кому (§13.1). */
export interface GrantHoldersConflictDetailsDto {
  holders: GrantHolderDto[];
}

/**
 * Коды отказов каталога — рядом с кодами нарушений и по той же причине: исход у каждого свой, и
 * различать их порталу нужно кодом, а не текстом.
 *
 * `version_conflict` здесь не перечислен: это общий код 409 (`err.conflict`), которым портал уже
 * показывает «откройте заново», и второго имени ему не нужно.
 */
export const GRANT_CONFLICT_CODES = {
  /** Правка или удаление системного набора: его состав живёт в коде (решение 2). */
  isSystem: 'grant_is_system',
  /** Удаление набора, у которого есть держатели: сначала отзыв (§13.1). */
  hasHolders: 'grant_has_holders',
  /** Код уже занят другим набором: он держится за набором навсегда, даже за удалённым. */
  codeTaken: 'grant_code_taken',
  /**
   * Отпечаток последствий не совпал: показанный предпросмотр больше не описывает то, что применится
   * (решение 7). Исход свой — «перечитайте и посмотрите предпросмотр заново», — и от `version_conflict`
   * он отличается тем, что состав набора мог вообще не меняться.
   */
  impactChanged: 'grant_impact_changed',
  /**
   * Роль держателя не входит в `grant_roles` набора: выдавать его этой роли нельзя (§13.1 — «новых
   * выдач этой роли уже не будет»).
   *
   * 409, а не 400: список наборов в карточке учётки отфильтрован совместимостью с ролью (§12), и
   * запрос, назвавший несовместимую пару, описывает не ошибку ввода, а устаревший экран — роль сменили
   * либо роль убрали из набора уже после того, как список показали.
   */
  roleNotAllowed: 'grant_role_not_allowed',
} as const;

// ── Предпросмотр последствий и результат назначения (решение 7; план §12, §13.1) ──

/** Что операция изменит у одной затронутой учётки. */
export interface GrantImpactUserDto {
  userId: string;
  fullName: string;
  role: Role | null;
  /**
   * Права, которые **реально** появятся и **реально** уйдут (`effectiveDelta`), — не разница составов
   * наборов. Снятие набора отбирает право только если его не даёт больше никто: ни роль, ни второй
   * набор, ни тип контрагента. Обе половины пустые — «доступ у этой учётки не изменится», и такая
   * строка в предпросмотре нужна: администратор видит, что человек затронут операцией, но ничего не
   * теряет.
   */
  added: Permission[];
  removed: Permission[];
  /** Роль держателя вне `grant_roles`: набор ему уже не даёт прав, и дельта у него пустая (§13.1). */
  roleMismatch: boolean;
}

/**
 * Предпросмотр последствий: что изменится и отпечаток того, из чего это посчитано (§13.1).
 *
 * Нарушения приходят в теле, а не отказом: предпросмотр — это чтение, и «так сохранить нельзя» он
 * обязан показать до нажатия, а не вместо ответа. Делятся они на два вида по той же границе, что в
 * `validateGrantAssignment`: `violations` — про сам набор, `holders` — про итог у затронутых учёток.
 */
export interface GrantImpactDto {
  operation: GrantImpactOperation;
  grantId: string;
  grantCode: string;
  grantName: string;
  /** Цель у выдачи и отзыва; `null` у правки набора. */
  userId: string | null;
  /** Версия набора, вошедшая в отпечаток. */
  version: number;
  /**
   * Затронутые учётки — то самое «затронет 14 учёток» из §12. У выдачи и отзыва их всегда ровно
   * одна, у правки набора — все держатели, включая тех, у кого дельта пустая.
   */
  users: GrantImpactUserDto[];
  /** Барьеры набора, которые операция нарушит; пустой список — «сохранить можно». */
  violations: GrantViolation[];
  /** Барьеры итога по каждой виновной учётке. */
  holders: GrantHolderViolationsDto[];
  /** Отпечаток: его же операция принимает в `expectedImpactHash`. */
  expectedImpactHash: string;
}

/**
 * Итог выдачи или отзыва.
 *
 * Карточка набора идёт в ответе целиком, а не одним `assignmentId`: реестр выдач обязан показывать
 * актуальное сразу после операции (§12), и второй запрос за ним разошёлся бы с первым ровно в тот
 * момент, когда рядом работает второй администратор.
 */
export interface GrantAssignmentResultDto {
  /**
   * Строка назначения. При повторной выдаче того же набора — **прежняя**, с прежним автором и
   * временем: `id` неизменяем, на нём держится откат перевода ролей (решение 3). У отзыва — та, что
   * удалена; переиспользовать её значение нельзя ни при каких обстоятельствах.
   */
  assignmentId: string;
  userId: string;
  grantId: string;
  /**
   * Изменилось ли состояние. Повторная выдача уже выданного набора отвечает `false`: дубля не
   * возникает (`UNIQUE (user_id, grant_id)`), токены не гаснут, в журнал ничего не уходит.
   */
  changed: boolean;
  /** Что реально изменилось в правах учётки — то же, что показывал предпросмотр. */
  delta: PermissionDelta;
  /** Набор с обновлённым реестром выдач. */
  grant: GrantCardDto;
}
